/**
 * ============================================================================
 * TASKPANE.TS - MAIN OUTLOOK ADD-IN LOGIC
 * ============================================================================
 * This is the central brain of our Outlook add-in. It handles:
 * 1) Authenticating the user against Microsoft Graph (via MSAL).
 * 2) Fetching Sites, Libraries, and Folders to let the user pick a destination.
 * 3) Managing the UI state (breadcrumb navigation, error messages, etc.).
 * 4) Grabbing attachments from the current email and uploading them to SharePoint.
 */
import { PublicClientApplication } from "@azure/msal-browser";
import { sharePointConfigSiteId, sharePointConfigListId, sharePointLogSiteId, sharePointLogListId } from "./config";


/************************************************************************
 * 1. GLOBAL STATE & CONFIGURATION
 ************************************************************************/

// ============================================================================
// MSAL (Microsoft Authentication Library) Configuration
// ============================================================================
// This tells MSAL how to talk to Azure AD to get our tokens.
// Make sure the clientId matches your App Registration in Azure Portal!
const msalConfig = {
  auth: {
    clientId: "7a2ae3a0-61f8-4bac-bd98-566bf3c1dda9",
    authority: "https://login.microsoftonline.com/3c90a2ff-691c-483a-8e94-fbca1b7d4edf", //tenant ID
  },
};
const pca = new PublicClientApplication(msalConfig);
let driveId = "";
// State management
let selectionMode: "site" | "library" | "folder" = "site";
let currentSiteId = "";
let currentSiteName = "";
let currentLibraryId = "";
let currentLibraryName = "";
let breadcrumbHistory: { id: string; name: string; type: "site" | "library" | "folder" }[] = [];
let currentFolderId = "root";
let attachments: (Office.AttachmentDetails & { checked: boolean })[] = [];
let previewMode = false;
let searchTimeout: number | null = null;
let messageTimeout: number | null = null;
// To manage the timeout for no-subfolder-message
let uploadStatusTimeout: number | null = null;
// To manage the timeout for upload status messages
let lastSelectedFolderRow: HTMLTableRowElement | null = null;
// To keep track of the currently selected folder row element
let selectedFolderCrumb: { id: string; name: string } | null = null;
// The folder that is currently selected (single-clicked) but not yet navigated into
let cachedEmlContent: string | null = null;
// Cache the pre-generated EML content to calculate size and avoid double generation
let currentViewItems: any[] = [];
// Stores the current items for lightning-fast dynamic local search

let customColumnsConfig: any[] = [];
let currentMetadataValues: Record<string, any> | null = null;
let hasCustomColumnsFetched = false;
// Cached DOM elements for frequent access
// These will be initialized once in Office.onReady to minimize repeated DOM lookups

// File type icons mapping for Font Awesome
const fileIcons: Record<string, string> = {
  pdf: "file-pdf",
  doc: "file-word",
  docx: "file-word",
  xls: "file-excel",
  xlsx: "file-excel",
  ppt: "file-powerpoint",
  pptx: "file-powerpoint",
  txt: "file-alt",
  jpg: "file-image",
  jpeg: "file-image",
  png: "file-image",
  gif: "file-image",
  mp3: "file-audio",
  wav: "file-audio",
  mp4: "file-video",
  avi: "file-video",
  mov: "file-video",
  zip: "file-archive",
  rar: "file-archive",
  exe: "file-code",
  xml: "file-code",
  eml: "envelope", // Added for .eml files (item attachments)
  msg: "envelope", // Added for .msg files (if applicable)
};
// MIME types mapping for upload (more efficient than a switch statement)
const mimeTypes = new Map<string, string>([
  ["pdf", "application/pdf"],
  ["doc", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"],
  ["docx", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"],
  ["xls", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"],
  ["xlsx", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"],
  ["ppt", "application/vnd.openxmlformats-officedocument.presentationml.presentation"],
  ["pptx", "application/vnd.openxmlformats-officedocument.presentationml.presentation"],
  ["txt", "text/plain"],
  ["jpg", "image/jpeg"],
  ["jpeg", "image/jpeg"],
  ["png", "image/png"],
  ["gif", "image/gif"],
  ["mp3", "audio/mpeg"],
  ["wav", "audio/wav"],
  ["mp4", "video/mp4"],
  ["avi", "video/x-msvideo"],
  ["mov", "video/quicktime"],
  ["zip", "application/zip"],
  ["rar", "application/x-rar-compressed"],
  ["eml", "message/rfc822"],
  ["msg", "application/vnd.ms-outlook"],
]);
// --- Favorites Configuration Logic ---

const discoveredSiteCache: { [key: string]: string } = {};
let createFolderMessageTimeout: number | undefined;
let hasInitializedData = false;

/************************************************************************
 * 2. INITIALIZATION & LIFECYCLE
 ************************************************************************/

// Initialize the add-in with error handling - enhanced DOM safety and event listener robustness
Office.onReady(async () => {
  showProcessingMessage("Initializing add-in...");
  try {
    // Display version information
    const versionDisplay = getHTMLElement("version-display");
    if (versionDisplay) {
      versionDisplay.textContent = `v${APP_VERSION}`;
    }

    // Populate welcome strip with user's first name
    const welcomeText = getHTMLElement("welcome-text");
    if (welcomeText) {
      try {
        const displayName = Office.context?.mailbox?.userProfile?.displayName || "";
        const firstName = displayName.split(" ")[0] || "";
        welcomeText.textContent = firstName ? `Welcome, ${firstName}` : "Welcome";
      } catch {
        welcomeText.textContent = "Welcome";
      }
    }

    // Check if version has changed
    checkVersionChange();

    const folderContainer = getHTMLElement("folder-container");

    const searchContainer = getHTMLElement("search-container");
    const breadcrumbRow = getHTMLElement("breadcrumb-row");
    /**
 * Creates a new subfolder in the currently selected SharePoint location.
 */
const createFolderInputContainer = getHTMLElement("create-folder-input-container");

    // Cache elements on initial load

    // Safely hide all containers initially
    folderContainer?.classList.add("hidden");
    searchContainer?.classList.add("hidden");
    breadcrumbRow?.classList.add("hidden");
    createFolderInputContainer?.classList.add("hidden");

    // Removed full header selectors in favor of binding directly to icons
    // const toggleFolderSelection = getHTMLElement("toggle-folder-selection");
    // const toggleFolderName = getHTMLElement("toggle-folder-name");
    const selectionBody = getHTMLElement("folder-selection-body");
    const selectionIcon = getHTMLElement("folder-selection-icon");
    const nameIcon = getHTMLElement("folder-name-icon");

    const toggleFolderSection = async () => {
      if (!hasInitializedData) {
        await initializeView();
        hasInitializedData = true;
        updateUploadButtonState();
        updateSelectionBodyVisibility();
        folderContainer?.classList.remove("hidden");
        if (selectionIcon) selectionIcon.className = "fas fa-chevron-down";
        if (nameIcon) nameIcon.className = "fas fa-chevron-down";

        getHTMLElement("search-container")?.classList.remove("hidden");
        getHTMLElement("breadcrumb-row")?.classList.remove("hidden");
        getHTMLElement("change-site-link-container")?.classList.remove("hidden");
        updateDynamicUIText();
      }
    };

    if (selectionIcon) {
      selectionIcon.addEventListener("click", async (e) => {
        e.stopPropagation(); // Prevent any parent clicks if they exist
        if (selectionMode === "site") return; // Do not toggle when selecting a site

        if (!hasInitializedData) {
          await toggleFolderSection();
        } else {
          const isHidden = selectionBody?.classList.contains("hidden");
          if (isHidden) {
            updateSelectionBodyVisibility();
          } else {
            selectionBody?.classList.add("hidden");
            if (selectionIcon) selectionIcon.className = "fas fa-chevron-right";
          }
        }
      });
    }

    if (nameIcon) {
      nameIcon.addEventListener("click", async (e) => {
        e.stopPropagation();
        if (!hasInitializedData) {
          await toggleFolderSection();
        } else {
          const isHidden = folderContainer?.classList.contains("hidden");
          if (isHidden) {
            folderContainer?.classList.remove("hidden");
            if (nameIcon) nameIcon.className = "fas fa-chevron-down";
          } else {
            folderContainer?.classList.add("hidden");
            if (nameIcon) nameIcon.className = "fas fa-chevron-right";
          }
        }
      });
    }

    const uploadBtn = getHTMLElement("upload-btn") as HTMLButtonElement;
    if (uploadBtn) uploadBtn.addEventListener("click", uploadAttachments);

    const toggleCreateFolderIcon = getHTMLElement("create-folder-icon");
    if (toggleCreateFolderIcon) {
      toggleCreateFolderIcon.addEventListener("click", (e) => {
        e.stopPropagation();
        const container = getHTMLElement("create-folder-input-container");
        const icon = getHTMLElement("create-folder-icon");
        if (container) {
          const isHidden = container.classList.contains("hidden");
          if (isHidden) {
            container.classList.remove("hidden");
            if (icon) icon.className = "fas fa-chevron-down";
            /**
 * Creates a new subfolder in the currently selected SharePoint location.
 */
const createFolderNameInput = getHTMLElement("create-folder-name") as HTMLInputElement;
            if (createFolderNameInput) createFolderNameInput.value = "";
          } else {
            container.classList.add("hidden");
            if (icon) icon.className = "fas fa-chevron-right";
          }
        }
      });
    }

    const createBtn = getHTMLElement("create-btn") as HTMLButtonElement;
    if (createBtn) createBtn.addEventListener("click", createFolder);

    /**
 * Closes the metadata modal.
 */
const closeMetadataModalBtn = getHTMLElement("close-metadata-modal");
    if (closeMetadataModalBtn) closeMetadataModalBtn.addEventListener("click", closeMetadataModal);

    /**
 * Saves the data entered in the metadata modal and proceeds with the upload.
 */
const saveMetadataBtn = getHTMLElement("save-metadata-btn");
    if (saveMetadataBtn) saveMetadataBtn.addEventListener("click", saveMetadata);

    const folderSearchInput = getHTMLElement("folder-search") as HTMLInputElement;
    if (folderSearchInput) folderSearchInput.addEventListener("input", searchFolders);

    const selectAllAttachmentsCheckbox = getHTMLElement("select-all-attachments") as HTMLInputElement;
    if (selectAllAttachmentsCheckbox)
      selectAllAttachmentsCheckbox.addEventListener("change", toggleSelectAllAttachments);

    const saveFavCb = getHTMLElement("save-favorite-checkbox") as HTMLInputElement;
    if (saveFavCb) {
      // Toggle: check = save favorite, uncheck = delete favorite & reset to site selection
      saveFavCb.addEventListener("change", onSaveFavoriteToggle);
    }

    const configGear = getHTMLElement("site-config-gear");
    if (configGear) {
      configGear.addEventListener("click", () => {
        resetToSiteSelection();
      });
    }

    // Wire "Change Site / Library" link
    const changeSiteLink = getHTMLElement("change-site-link");
    if (changeSiteLink) {
      changeSiteLink.addEventListener("click", async (e) => {
        e.preventDefault();
        await resetToSiteSelection();
      });
    }

    await previewAttachments();

    // Unconditionally auto-initialize the view on startup
    if (!hasInitializedData) {
      await toggleFolderSection();
    }

    updateUploadButtonState();
  } catch (error) {
    console.error("Office.onReady initialization error:", error);
    showResult("Failed to initialize the add-in. Please restart Outlook.", "error");
  } finally {
    const mainContent = getHTMLElement("main-content");
    const welcomeStrip = getHTMLElement("welcome-strip");
    if (mainContent) mainContent.classList.remove("hidden");
    if (welcomeStrip) welcomeStrip.classList.remove("hidden");
    hideProcessingMessage();
  }
});
/**
 * Initializes the main view of the add-in, loading attachments and attempting to load favorites.
 */
const initializeView = async () => {
  try {
    const hasFav = await loadFavoriteConfiguration();
    if (hasFav && currentSiteId && currentLibraryId) {
      // Favorite found – jump straight to folder view
      breadcrumbHistory = [
        { id: currentSiteId, name: currentSiteName, type: "site" },
        { id: currentLibraryId, name: currentLibraryName, type: "library" },
        { id: "root", name: "Root", type: "folder" },
      ];
      currentFolderId = "root";
      updateBreadcrumb();
      updateSelectionDisplay("Root");
      showChangeSiteLink(true);
      showSaveFavorite(false); // Favorite already saved - hide the checkbox
      updateUploadButtonState(); // ← Enable button immediately (selectionMode = "folder")
      await loadFolders("root", "Root");
    } else {
      // No favorite or incomplete – show site list for manual selection
      selectionMode = "site";
      currentSiteId = "";
      currentSiteName = "";
      currentLibraryId = "";
      currentLibraryName = "";
      driveId = "";
      breadcrumbHistory = [];
      updateBreadcrumb();
      showChangeSiteLink(false);
      showSaveFavorite(false); // no favorite yet
      const saveFavCb = getHTMLElement("save-favorite-checkbox") as HTMLInputElement;
      if (saveFavCb) saveFavCb.checked = false;

      const sites = await fetchSites();
      updateFolderTable(sites, "root");
      updateSelectionDisplay();
      updateUploadButtonState();
    }
  } catch (err) {
    console.error("initializeView error:", err);
    // Safe fallback: show site list
    selectionMode = "site";
    breadcrumbHistory = [];
    updateBreadcrumb();
    showChangeSiteLink(false);
    showSaveFavorite(false); // fallback
    const saveFavCb = getHTMLElement("save-favorite-checkbox") as HTMLInputElement;
    if (saveFavCb) saveFavCb.checked = false;

    const sites = await fetchSites().catch(() => []);
    updateFolderTable(sites, "root");
    updateUploadButtonState(); // ← Disable button in fallback state
  }
};
// Check if app version has changed and notify user
/**
 * Checks if the add-in version has changed since the last run to show update notifications.
 */
const checkVersionChange = () => {
  try {
    const lastSeenVersion = localStorage.getItem("lastSeenVersion");

    if (lastSeenVersion !== APP_VERSION) {
      // Version has changed or is new, create a notification that appears at the top
      const body = document.getElementById("app-body") || document.body;
      if (body) {
        const notification = document.createElement("div");
        notification.className = "version-notification info";
        notification.textContent = `App updated to version ${APP_VERSION}. Check for new features!`;

        // Insert at the top of the body
        body.insertBefore(notification, body.firstChild);

        // Remove after 5 seconds
        setTimeout(() => {
          notification.classList.add("fade-out");
          setTimeout(() => {
            notification.remove();
          }, 500); // Extra time for fade-out animation
        }, 10000);
      } else {
        // Fallback to the regular notification if body not found
        showResult(`App updated to version ${APP_VERSION}. Check for new features!`, "info");
      }
    }

    // Store current version for next time
    localStorage.setItem("lastSeenVersion", APP_VERSION);
  } catch (error) {
    console.error("Error checking version:", error);
  }
};

/************************************************************************
 * 3. AUTHENTICATION & TOKEN MANAGEMENT
 ************************************************************************/

// Get access token with enhanced error handling and silent renewal
/**
 * Retrieves a valid MSAL access token, using silent renewal or prompting the user if necessary.
 * @returns A promise resolving to the access token string.
 */
const getAccessToken = async (): Promise<string> => {
  try {
    // Define token request
    const request = { scopes: ["Files.ReadWrite.All", "Sites.ReadWrite.All", "User.Read"] };

    // Check for existing valid token with 5-minute buffer before expiry
    const accessToken = sessionStorage.getItem("accessToken");
    const tokenExpiry = sessionStorage.getItem("tokenExpiry");
    const EXPIRY_BUFFER_MS = 5 * 60 * 1000; // 5 minutes buffer

    if (accessToken && tokenExpiry && Date.now() < Number(tokenExpiry) - EXPIRY_BUFFER_MS) {
      return accessToken;
    }

    // Initialize MSAL if needed
    try {
      await pca.initialize();
    } catch (initError) {
      console.warn("MSAL initialization warning, may already be initialized:", initError);
    }

    // Try silent token acquisition first
    try {
      showProcessingMessage("Authenticating...", true);

      const accounts = pca.getAllAccounts();
      const userEmail = Office.context?.mailbox?.userProfile?.emailAddress;

      if (accounts.length > 0) {
        // Try silent token acquisition
        const silentRequest = {
          scopes: request.scopes,
          account: accounts[0],
          // Do NOT force refresh — use cached token if still valid
        };

        const silentResult = await pca.acquireTokenSilent(silentRequest);
        if (silentResult && silentResult.accessToken) {
          // Store the new token
          sessionStorage.setItem("accessToken", silentResult.accessToken);
          sessionStorage.setItem("tokenExpiry", new Date(silentResult.expiresOn!).getTime().toString());
          hideProcessingMessage();
          return silentResult.accessToken;
        }
      } else if (userEmail) {
        // Try ssoSilent using the current mailbox user's email address
        const silentResult = await pca.ssoSilent({
          scopes: request.scopes,
          loginHint: userEmail,
        });
        if (silentResult && silentResult.accessToken) {
          sessionStorage.setItem("accessToken", silentResult.accessToken);
          sessionStorage.setItem("tokenExpiry", new Date(silentResult.expiresOn!).getTime().toString());
          hideProcessingMessage();
          return silentResult.accessToken;
        }
      }
    } catch (silentError) {
      console.log("Silent token acquisition failed, falling back to interactive login", silentError);
      // Continue to interactive login
    }

    // If silent acquisition fails, use interactive login
    hideProcessingMessage();

    try {
      const userEmail = Office.context?.mailbox?.userProfile?.emailAddress;
      const popupRequest = {
        scopes: request.scopes,
        loginHint: userEmail || undefined,
      };

      try {
        const loginResponse = await pca.acquireTokenPopup(popupRequest);
        if (!loginResponse.accessToken) {
          throw new Error("No access token received from dialog.");
        }
        // Store the new token
        sessionStorage.setItem("accessToken", loginResponse.accessToken);
        if (loginResponse.expiresOn) {
          sessionStorage.setItem("tokenExpiry", loginResponse.expiresOn.getTime().toString());
        }
        return loginResponse.accessToken;
      } catch (popupErr: any) {
        if (popupErr.message && popupErr.message.includes("popup_window_error")) {
          // The browser blocked the popup because it wasn't triggered by a user click.
          // Show a Sign In button overlay to get a trusted click event.
          return await new Promise<string>((resolve, reject) => {
            const overlay = document.createElement("div");
            overlay.style.position = "fixed";
            overlay.style.top = "0";
            overlay.style.left = "0";
            overlay.style.width = "100%";
            overlay.style.height = "100%";
            overlay.style.backgroundColor = "rgba(255, 255, 255, 0.95)";
            overlay.style.display = "flex";
            overlay.style.flexDirection = "column";
            overlay.style.justifyContent = "center";
            overlay.style.alignItems = "center";
            overlay.style.zIndex = "9999";

            const msg = document.createElement("p");
            msg.textContent = "Sign in is required to connect to SharePoint.";
            msg.style.marginBottom = "20px";
            msg.style.fontFamily = "var(--font-family, 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif)";
            msg.style.color = "#333";
            msg.style.fontWeight = "500";

            const btn = document.createElement("button");
            btn.textContent = "Sign In to SharePoint";
            btn.className = "action-btn"; // Match existing add-in button style
            btn.style.padding = "10px 20px";
            btn.style.fontSize = "14px";
            btn.style.cursor = "pointer";
            btn.style.backgroundColor = "var(--primary-color, #6264a7)";
            btn.style.color = "white";
            btn.style.border = "none";
            btn.style.borderRadius = "4px";

            btn.onclick = async () => {
              btn.textContent = "Signing In...";
              btn.disabled = true;
              btn.style.opacity = "0.7";
              try {
                const manualLoginResponse = await pca.acquireTokenPopup(popupRequest);
                if (manualLoginResponse.accessToken) {
                  sessionStorage.setItem("accessToken", manualLoginResponse.accessToken);
                  if (manualLoginResponse.expiresOn) {
                    sessionStorage.setItem("tokenExpiry", manualLoginResponse.expiresOn.getTime().toString());
                  }
                  document.body.removeChild(overlay);
                  resolve(manualLoginResponse.accessToken);
                } else {
                  throw new Error("No access token received.");
                }
              } catch (manualErr) {
                document.body.removeChild(overlay);
                reject(manualErr);
              }
            };

            overlay.appendChild(msg);
            overlay.appendChild(btn);
            document.body.appendChild(overlay);
            hideProcessingMessage();
          });
        }
        throw popupErr; // If it's not a popup blocker error, throw it normally
      }
    } catch (error: any) {
      console.error("Authentication failed during interactive dialog:", error);
      throw new Error(`Authentication failed: ${error.message || JSON.stringify(error)}`);
    }
  } catch (error: any) {
    console.error("Token acquisition failed overall:", error);
    throw error;
  } finally {
    hideProcessingMessage();
  }
};

/************************************************************************
 * 4. SHAREPOINT & GRAPH API INTERACTIONS (DATA FETCHING)
 ************************************************************************/

/**
 * ============================================================================
 * fetchWithRetry
 * ============================================================================
 * A handy wrapper around the native `fetch` that automatically retries
 * requests if we hit transient errors (like 429 Too Many Requests or 5xx server issues).
 * It uses exponential backoff so we don't hammer the server.
 * 
 * @param url The URL to fetch.
 * @param options Request options.
 * @param retries Number of retries (default 3).
 * @param delay Initial delay in ms for exponential backoff (default 1000).
 * @returns Promise<Response>
 */
/**
 * Fetches a URL with built-in retry logic for handling transient network failures or rate limits.
 * @param url The endpoint to fetch.
 * @param options Fetch options including headers.
 * @param retries Number of remaining retries.
 * @param delay Delay before the next retry in milliseconds.
 * @returns The fetch Response object.
 */
const fetchWithRetry = async (
  url: string,
  options: RequestInit,
  retries: number = 3,
  delay: number = 1000 // Initial delay in ms
): Promise<Response> => {
  try {
    const response = await fetch(url, options);

    // If response is not OK, and it's a retryable status code
    if (!response.ok) {
      // Common transient/retryable status codes: 429 (Too Many Requests), 5xx (Server Errors)
      const isRetryableStatus = [429, 500, 502, 503, 504].includes(response.status);

      if (retries > 0 && isRetryableStatus) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error?.message || response.statusText || "Unknown error";
        console.warn(
          `Attempt failed for ${url} (Status: ${response.status}, Message: ${errorMessage}). Retrying in ${delay}ms. Remaining retries: ${retries}`
        );
        await new Promise((res) => setTimeout(res, delay));
        return fetchWithRetry(url, options, retries - 1, delay * 2); // Exponential backoff
      }
    }
    return response; // Return original response if OK or no retries left
  } catch (error) {
    // Catch network errors (e.g., no internet, DNS issues)
    if (retries > 0) {
      console.warn(
        `Workspace failed for ${url} (Network error). Retrying in ${delay}ms. Remaining retries: ${retries}`,
        error
      );
      return fetchWithRetry(url, options, retries - 1, delay * 2);
    }
    throw error; // Re-throw if no retries left
  }
};

// --- Site & Library Fetching ---
/**
 * Fetches available SharePoint sites for the current user, optionally filtered by a search query.
 * @param searchQuery Optional search query to filter sites.
 * @returns A promise resolving to an array of site objects.
 */
const fetchSites = async (searchQuery: string = ""): Promise<any[]> => {
  showProcessingMessage("Loading accessible sites...");
  try {
    const accessToken = await getAccessToken();
    const query = searchQuery ? searchQuery : "*";
    let url = `https://graph.microsoft.com/v1.0/sites?search=${encodeURIComponent(query)}`;

    const response = await fetchWithRetry(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (!response.ok) {
      let errorDetails = "";
      try {
        const errData = await response.json();
        errorDetails = errData.error?.message || JSON.stringify(errData);
      } catch (e) {
        errorDetails = await response.text();
      }
      throw new Error(`${response.status} ${response.statusText} - ${errorDetails}`);
    }

    const data = await response.json();
    const sites = data.value || [];

    showProcessingMessage("Verifying site access (this may take a moment)...");

    // For each site, fetch its drives and check if AT LEAST ONE drive allows write access.
    // If a site has 0 writable drives, it is completely hidden.
    const verifiedSites: any[] = [];
    await Promise.all(
      sites.map(async (site: any) => {
        try {
          const drivesUrl = `https://graph.microsoft.com/v1.0/sites/${site.id}/drives`;
          const drivesRes = await fetchWithRetry(drivesUrl, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });

          if (drivesRes.ok) {
            const drivesData = await drivesRes.json();
            const drives = drivesData.value || [];

            // We only need ONE writable library to show the site
            let hasWritableLibrary = false;

            // To prevent massive concurrent requests on huge sites, we loop synchronously
            // but we break early as soon as we find a writable one.
            for (const drive of drives) {
              const canWrite = await checkWriteAccess(drive.id, accessToken);
              if (canWrite) {
                hasWritableLibrary = true;
                break;
              }
            }

            if (hasWritableLibrary) {
              verifiedSites.push(site);
            }
          }
        } catch (e) {
          console.warn(`Failed to verify write access for site ${site.name}`, e);
        }
      })
    );

    currentViewItems = verifiedSites;
    return currentViewItems;
  } catch (error: any) {
    console.error("Error fetching sites:", error);
    showResult(`Error fetching sites: ${error.message}`, "error");
    return [];
  } finally {
    hideProcessingMessage();
  }
};

/**
 * Fetches document libraries within a specific SharePoint site.
 * @param siteId The ID of the SharePoint site.
 * @returns A promise resolving to an array of document libraries.
 */
const fetchLibraries = async (siteId: string): Promise<any[]> => {
  showProcessingMessage("Loading document libraries...");
  try {
    const accessToken = await getAccessToken();
    let url = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives`;
    const response = await fetchWithRetry(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (!response.ok) throw new Error(`Failed to fetch libraries: ${response.statusText}`);
    const data = await response.json();
    const drives = data.value || [];

    showProcessingMessage("Verifying library access...");

    // Filter out libraries where the user only has Read access
    const verifiedLibraries: any[] = [];
    await Promise.all(
      drives.map(async (drive: any) => {
        const canWrite = await checkWriteAccess(drive.id, accessToken);
        if (canWrite) {
          verifiedLibraries.push(drive);
        }
      })
    );

    currentViewItems = verifiedLibraries;
    return currentViewItems;
  } catch (error) {
    console.error("Error fetching libraries:", error);
    showResult("Error fetching libraries", "error");
    return [];
  } finally {
    hideProcessingMessage();
  }
};

// Fetch folders from Graph API with improved token handling
/**
 * Fetches subfolders from SharePoint for the specified folder or library ID.
 * @param folderId The ID of the parent folder.
 * @param searchQuery Optional search query to filter folders.
 * @returns A promise resolving to an array of folder objects.
 */
const fetchFolders = async (folderId: string = "root", searchQuery: string = ""): Promise<any[]> => {
  showProcessingMessage("Loading folders...");
  try {
    // Get access token with automatic renewal if needed
    const accessToken = await getAccessToken();
    let url = `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${folderId}/children?$select=id,name,folder`;

    if (searchQuery) {
      url += `&$filter=startswith(name, '${encodeURIComponent(searchQuery)}')`;
    }

    const response = await fetchWithRetry(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error?.message || response.statusText || "Unknown error";

      // Handle authentication errors with automatic token refresh
      if (response.status === 401) {
        console.log("Authentication error, clearing token cache and retrying");
        sessionStorage.removeItem("accessToken");
        sessionStorage.removeItem("tokenExpiry");

        // Try once more with a fresh token
        const newToken = await getAccessToken();
        const retryResponse = await fetchWithRetry(url, {
          headers: { Authorization: `Bearer ${newToken}` },
        });

        if (retryResponse.ok) {
          const retryData = await retryResponse.json();
          if (retryData && Array.isArray(retryData.value)) {
            return retryData.value.filter((item: any) => item.folder);
          }
        }

        throw new Error(`Authentication error: ${errorMessage}. Please re-authenticate.`);
      }

      if (response.status === 403) {
        throw new Error(`Permission denied: ${errorMessage}. You may not have access to this drive or folder.`);
      }
      if (response.status === 404) {
        throw new Error(`Resource not found: ${errorMessage}. The specified drive or folder might not exist.`);
      }
      throw new Error(`HTTP error! status: ${response.status} - ${errorMessage}`);
    }

    const data = await response.json();
    if (!data || !Array.isArray(data.value)) {
      throw new Error("Invalid response from Graph API: 'value' array missing.");
    }
    const folders = data.value.filter((item: any) => item.folder);

    showProcessingMessage("Verifying folder access (this may take a moment)...");
    const verifiedFolders: any[] = [];
    const BATCH_SIZE = 5;
    for (let i = 0; i < folders.length; i += BATCH_SIZE) {
      const batch = folders.slice(i, i + BATCH_SIZE);
      const results = await Promise.all(
        batch.map(async (folder: any) => {
          const canWrite = await checkWriteAccess(driveId, accessToken, folder.id);
          return canWrite ? folder : null;
        })
      );
      verifiedFolders.push(...results.filter((f: any) => f !== null));
    }

    currentViewItems = verifiedFolders;
    return currentViewItems;
  } catch (error) {
    console.error("Error fetching folders:", error);
    showResult(error instanceof Error ? error.message : "Error fetching folders", "error");
    return [];
  } finally {
    hideProcessingMessage();
  }
};

// --- Write Access Verification ---
// Microsoft Graph GET /sites doesn't filter out read-only sites natively.
// To satisfy the strict "only show writable" requirement, we test if we can 
// initialize an upload session on the library.
/**
 * Checks if the current user has write access to the specified SharePoint location.
 * @param driveId The SharePoint document library drive ID.
 * @param accessToken The MSAL access token.
 * @param itemId The specific folder item ID to check.
 * @returns True if the user has write access.
 */
const checkWriteAccess = async (driveId: string, accessToken: string, itemId: string = "root"): Promise<boolean> => {
  try {
    const randomName = `_SaveToPoint_AccessTest_${Math.random().toString(36).substring(2, 15)}.tmp`;
    const testUrl = `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${itemId}:/${randomName}:/createUploadSession`;
    const response = await fetchWithRetry(testUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        item: { "@microsoft.graph.conflictBehavior": "fail" },
      }),
    });

    if (response.ok) {
      // User has Write access!
      const data = await response.json();
      if (data.uploadUrl) {
        // Cancel the upload session to clean up the temporary placeholder
        fetch(data.uploadUrl, { method: "DELETE" }).catch(() => {});
      }
      return true;
    }
    // 403 Forbidden -> User has NO Write access.
    // 409 Conflict -> Should never happen with a random name, but means file exists.
    return false;
  } catch (error) {
    return false; // Assume read-only on network/unknown error
  }
};

/**
 * Checks if the current user has write permissions for the specified document library.
 * @param libraryId The ID of the document library.
 * @returns True if the user has write permissions.
 */
const checkLibraryPermissions = async (libraryId: string): Promise<boolean> => {
  try {
    const accessToken = await getAccessToken();
    const url = `https://graph.microsoft.com/v1.0/drives/${libraryId}/root`;
    const response = await fetchWithRetry(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.ok;
  } catch (e) {
    return false;
  }
};

const fetchCustomColumns = async (): Promise<any[]> => {
  try {
    const accessToken = await getAccessToken();
    const url = `https://graph.microsoft.com/v1.0/drives/${driveId}/list/columns`;
    const response = await fetchWithRetry(url, { headers: { Authorization: `Bearer ${accessToken}` } });
    if (!response.ok) return [];
    const data = await response.json();
    const ignoredColumns = new Set([
      "Title",
      "FileLeafRef",
      "Created",
      "Author",
      "Modified",
      "Editor",
      "DocIcon",
      "ItemChildCount",
      "FolderChildCount",
      "ContentType",
      "LinkTitleNoMenu",
      "LinkTitle",
      "Edit",
      "_UIVersionString",
      "Attachments",
      "AppAuthor",
      "AppEditor",
      "Name",
      "ID",
      "MediaServiceImageTags",
      "MediaServiceOCR",
      "MediaServiceAutoTags",
      "MediaServiceDateTaken",
      "MediaServiceKeyPoints",
      "MediaServiceLocation",
      "MediaServiceEventContentId",
      "lcoomiad",
      "_ip_UnifiedAuditLogClassification",
      "Combine",
      "RepairDocument",
      "vti_x005f_canhasdefaulttheme",
      "vti_x005f_canhastheme",
      "vti_x005f_canhassharedborders",
      "vti_x005f_rtfmetadata",
      "_VirusStatus",
      "_VirusVendorID",
      "_VirusInfo",
      "AccessPolicy",
      "_CommentCount",
      "_LikeCount",
      "A2AMetadata",
      "_SyncToOneDrive",
      "ProgId",
      "ScopeId",
      "SortBehavior",
      "SyncClientId",
      "SyncClientVersion",
      "SharedWithUsers",
      "SharedWithDetails",
      "OData__CopySource",
      "OData__HasCopyDestinations",
      "OData__UIVersionString",
      "TaxCatchAll",
      "TaxCatchAllLabel",
    ]);
    return data.value.filter(
      (col: any) =>
        !col.readOnly &&
        !col.hidden &&
        !ignoredColumns.has(col.name) &&
        !col.name.startsWith("_") &&
        (col.text || col.choice || col.dateTime || col.boolean)
    );
  } catch (e) {
    console.error("Error fetching custom columns:", e);
    return [];
  }
};
/**
 * Resolves a hostname-based short site ID into a native SharePoint Graph API site ID.
 * @param shortSiteId The shortened site ID.
 * @param targetListId Optional list ID for contextual resolution.
 * @returns A promise resolving to the native site ID string.
 */
const resolveNativeSiteId = async (shortSiteId: string, targetListId?: string): Promise<string> => {
  try {
    const cachedKey = targetListId ? `trueSiteId_${targetListId}` : `trueSiteId_${shortSiteId}`;
    if (discoveredSiteCache[cachedKey]) return discoveredSiteCache[cachedKey];

    try {
      const lsCache = localStorage.getItem(cachedKey);
      if (lsCache) {
        discoveredSiteCache[cachedKey] = lsCache;
        return lsCache;
      }
    } catch (e) {
      console.warn("Failed to read from localStorage:", e);
    }

    const accessToken = await getAccessToken();

    if (targetListId) {
      const directCheck = await fetchWithRetry(
        `https://graph.microsoft.com/v1.0/sites/${shortSiteId}/lists/${targetListId}?$select=id`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );
      if (directCheck.ok) {
        try {
          localStorage.setItem(cachedKey, shortSiteId);
        } catch (e) {
          console.warn("Failed to set localStorage:", e);
        }
        discoveredSiteCache[cachedKey] = shortSiteId;
        return shortSiteId;
      }
    }

    if (targetListId) {
      console.log(`Starting Auto-Discovery for List ID: ${targetListId}`);
      showProcessingMessage("Locating destination...");

      const queryUrl = `https://graph.microsoft.com/v1.0/sites?search=*`;
      const sitesRes = await fetchWithRetry(queryUrl, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (sitesRes.ok) {
        const sitesData = await sitesRes.json();
        const sites = sitesData.value || [];

        for (const site of sites) {
          if (!site.id) continue;
          try {
            const listsRes = await fetchWithRetry(
              `https://graph.microsoft.com/v1.0/sites/${site.id}/lists?$select=id`,
              {
                headers: { Authorization: `Bearer ${accessToken}` },
              }
            );
            if (listsRes.ok) {
              const listsData = await listsRes.json();
              if (
                listsData.value &&
                listsData.value.some((l: any) => String(l.id).toLowerCase() === String(targetListId).toLowerCase())
              ) {
                console.log(`Auto-Discovery Match! Found on SiteId: ${site.id}`);
                try {
                  localStorage.setItem(cachedKey, site.id);
                } catch (e) {
                  console.warn("Failed to set localStorage:", e);
                }
                discoveredSiteCache[cachedKey] = site.id;
                return site.id;
              }
            }
          } catch (e) {
            console.warn("Error checking lists for site:", site.id, e);
          }
        }
      }
    }

    return shortSiteId;
  } catch (e) {
    return shortSiteId;
  } finally {
    hideProcessingMessage();
  }
};

/************************************************************************
 * 5. NAVIGATION & STATE MANAGEMENT
 ************************************************************************/

// Load folders into table with error handling - already robust
/**
 * Loads and displays folders for the specified folder ID, acting as the main entry point for navigation.
 * @param folderId The folder ID to load.
 * @param folderName The name of the folder.
 */
const loadFolders = async (folderId: string = "root", folderName: string = "Root") => {
  try {
    const folders = await fetchFolders(folderId);
    updateFolderTable(folders, folderId);
  } catch (error) {
    console.error("Error loading folders:", error);
    showResult("Failed to load folders. Please check your connection and try again.", "error");
  }
};
// Search folders dynamically - completely local and instantaneous
/**
 * Handles the folder search input, debouncing the request and fetching filtered results.
 */
const searchFolders = () => {
  try {
    const searchInput = getHTMLElement("folder-search") as HTMLInputElement;
    const query = searchInput?.value.trim().toLowerCase() || "";
    let items: any[] = [];

    if (!query) {
      items = currentViewItems;
    } else {
      items = currentViewItems.filter((item) =>
        (item.name || item.displayName || "").toLowerCase().includes(query)
      );
    }

    // Determine the appropriate pId based on selection mode
    let pId = "root";
    if (selectionMode === "library") pId = currentSiteId;
    else if (selectionMode === "folder") pId = currentFolderId;

    updateFolderTable(items, pId);
  } catch (error) {
    console.error("Error in search function:", error);
    showResult("An error occurred with the search functionality.", "error");
  }
};
// Select a folder for upload — does NOT navigate inside it, but DOES update breadcrumb to show selection
/**
 * Handles single-click selection of a folder without navigating into it.
 * @param folderId The ID of the selected folder.
 * @param folderName The name of the selected folder.
 */
const selectFolder = (folderId: string, folderName: string) => {
  try {
    currentFolderId = folderId;
    // Show the selected folder as the last (black, non-clickable) crumb in the breadcrumb
    selectedFolderCrumb = { id: folderId, name: folderName };
    updateBreadcrumb();
    updateSelectionDisplay(folderName);
    updateUploadButtonState();
  } catch (error) {
    console.error("Error selecting folder:", error);
    showResult("Error selecting folder. Please try again.", "error");
  }
};
// Navigate to folder and update breadcrumb - already robust

/**
 * Navigates into a selected folder, updating the breadcrumbs and fetching its subfolders.
 * @param folderId The ID of the destination folder.
 * @param folderName The name of the destination folder.
 */
const navigateToFolder = (folderId: string, folderName: string) => {
  try {
    clearSearchInput();

    // The folder is now a navigation point — clear any pending selection crumb
    selectedFolderCrumb = null;
    currentFolderId = folderId;
    breadcrumbHistory.push({ id: folderId, name: folderName, type: "folder" });
    updateBreadcrumb();
    updateSelectionDisplay(folderName);
    loadFolders(folderId, folderName);
  } catch (error) {
    showResult("Error navigating to folder. Please try again.", "error");
  }
};
/**
 * Navigates backward in the folder hierarchy when a breadcrumb is clicked.
 * @param index The index in the breadcrumb history to return to.
 */
const goBack = (index: number) => {
  try {
    if (index < breadcrumbHistory.length - 1) {
      clearSearchInput();
      selectedFolderCrumb = null; // Clear any pending selection when going back
      breadcrumbHistory = breadcrumbHistory.slice(0, index + 1);
      const folderCrumb = breadcrumbHistory[index];
      currentFolderId = folderCrumb.id;
      updateBreadcrumb();
      loadFolders(folderCrumb.id, folderCrumb.name);
      updateSelectionDisplay(folderCrumb.name);
      updateUploadButtonState();
    }
  } catch (error) {
    console.error("Error navigating back:", error);
  }
};
// Update breadcrumb navigation
/**
 * Updates the breadcrumb navigation UI based on the user's traversal history.
 */
const updateBreadcrumb = () => {
  try {
    const breadcrumb = getHTMLElement("breadcrumb");
    if (!breadcrumb) return;
    breadcrumb.innerHTML = "";

    if (breadcrumbHistory.length === 0 && !selectedFolderCrumb) {
      return;
    }

    // "Root" is hidden whenever a real subfolder exists in navigation history
    // OR the user has selected (single-clicked) a subfolder at the current level.
    const hasRealSubfolder =
      breadcrumbHistory.some((c) => c.type === "folder" && c.id !== "root") || selectedFolderCrumb !== null;

    // Build the list of crumbs from history (skip "Root" when subfolders exist)
    type VisibleCrumb = { id: string; name: string; type: "site" | "library" | "folder"; isVirtual: boolean };
    const visibleCrumbs: VisibleCrumb[] = breadcrumbHistory
      .filter((c) => !(c.type === "folder" && c.id === "root" && hasRealSubfolder))
      .map((c) => ({ ...c, isVirtual: false }));

    // Append selectedFolderCrumb as a virtual last crumb if it isn't already the last crumb
    if (selectedFolderCrumb) {
      const lastVisible = visibleCrumbs.length > 0 ? visibleCrumbs[visibleCrumbs.length - 1] : null;
      if (!lastVisible || lastVisible.id !== selectedFolderCrumb.id) {
        visibleCrumbs.push({
          id: selectedFolderCrumb.id,
          name: selectedFolderCrumb.name,
          type: "folder",
          isVirtual: true,
        });
      }
    }

    if (visibleCrumbs.length === 0) return;

    const fragment = document.createDocumentFragment();

    visibleCrumbs.forEach((crumbData, visibleIndex) => {
      // Map back to breadcrumbHistory for navigation (virtual crumbs have no history entry)
      const realIndex = crumbData.isVirtual
        ? -1
        : breadcrumbHistory.findIndex((c) => c.id === crumbData.id && c.type === crumbData.type);

      const crumb = document.createElement("span");

      const isLastVisible = visibleIndex === visibleCrumbs.length - 1;

      // Library is inert (black, non-clickable) when:
      //   - It is the last visible crumb, OR
      //   - currentFolderId is "root" (Root level → Library + Root are both inert)
      const isLibraryInertAtRoot = crumbData.type === "library" && currentFolderId === "root";

      // Virtual crumbs (selected-but-not-navigated folders) are always inert
      const isInert = isLastVisible || isLibraryInertAtRoot || crumbData.isVirtual;

      crumb.className = `crumb ${isInert ? "active" : ""}`;
      crumb.textContent = crumbData.name;
      crumb.dataset.id = crumbData.id;
      crumb.dataset.type = crumbData.type;

      if (!isInert) {
        crumb.onclick = () => {
          selectedFolderCrumb = null; // Clear any pending selection when navigating via breadcrumb
          if (crumbData.type === "site") {
            clearSearchInput();
            hasCustomColumnsFetched = false;
            currentMetadataValues = null;
            customColumnsConfig = [];
            // Clicking Site → show Library chooser for that site
            selectionMode = "library";
            currentLibraryId = "";
            currentLibraryName = "";
            breadcrumbHistory = breadcrumbHistory.slice(0, realIndex + 1);
            updateBreadcrumb();
            fetchLibraries(crumbData.id).then((libs) => updateFolderTable(libs, crumbData.id));
            updateSelectionDisplay();
            updateUploadButtonState();
          } else if (crumbData.type === "library") {
            clearSearchInput();
            // Clicking Library (reachable only when inside subfolders) →
            // Navigate back to [Site > Library > Root], Library + Root both inert
            selectionMode = "folder";
            currentFolderId = "root";
            breadcrumbHistory = breadcrumbHistory.slice(0, realIndex + 1);
            breadcrumbHistory.push({ id: "root", name: "Root", type: "folder" });
            updateBreadcrumb();
            loadFolders("root", "Root");
            updateSelectionDisplay("Root");
            updateUploadButtonState();
          } else if (crumbData.type === "folder") {
            if (realIndex >= 0) {
              if (realIndex < breadcrumbHistory.length - 1) {
                // Not the last navigation crumb → use goBack to trim everything deeper
                goBack(realIndex);
              } else {
                // IS the last navigation crumb, but was rendered clickable because
                // selectedFolderCrumb (a single-clicked folder) was appended after it.
                // Just re-select this folder — table already shows its children.
                currentFolderId = crumbData.id;
                if (lastSelectedFolderRow) {
                  lastSelectedFolderRow.classList.remove("selected-folder-row");
                  lastSelectedFolderRow = null;
                }
                updateBreadcrumb();
                updateSelectionDisplay(crumbData.name);
                updateUploadButtonState();
              }
            }
          }
        };
      }

      fragment.appendChild(crumb);
      if (visibleIndex < visibleCrumbs.length - 1) {
        const divider = document.createElement("span");
        divider.className = "divider";
        divider.textContent = " > ";
        fragment.appendChild(divider);
      }
    });

    breadcrumb.appendChild(fragment);
  } catch (error) {
    console.error("Error updating breadcrumb:", error);
  }
};

/************************************************************************
 * 6. EML GENERATION & FILE PROCESSING
 ************************************************************************/

// Get email content as EML with improved error handling and type safety
/**
 * Generates an EML format string containing the current email body and attachments using native Office.js APIs.
 * @returns A promise resolving to the EML string.
 */
const getEmailAsEML = async (): Promise<string> => {
  return new Promise((resolve, reject) => {
    try {
      Office.context.mailbox.item.body.getAsync(Office.CoercionType.Text, async (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          const item = Office.context.mailbox.item;
          if (!item) {
            throw new Error("Could not access email item");
          }

          const headers: string[] = [];
          const boundary = "----=_NextPart_" + Math.random().toString(36).substring(2);

          // Add comprehensive email headers with null checks
          const fromAddress = item.from
            ? `${item.from.displayName} <${item.from.emailAddress || "Unknown"}>`
            : "Unknown Sender";
          headers.push(`From: ${fromAddress}`);

          // Add To recipients with type safety
          const toRecipients = (item.to as unknown as Office.EmailAddressDetails[]) || [];
          if (toRecipients.length > 0) {
            const toAddresses = toRecipients
              .filter((r) => r && r.emailAddress) // Filter out invalid recipients
              .map((r) => `${r.displayName || r.emailAddress} <${r.emailAddress}>`)
              .join(", ");
            if (toAddresses) headers.push(`To: ${toAddresses}`);
          }

          // Add CC recipients with type safety
          const ccRecipients = (item.cc as unknown as Office.EmailAddressDetails[]) || [];
          if (ccRecipients.length > 0) {
            const ccAddresses = ccRecipients
              .filter((r) => r && r.emailAddress)
              .map((r) => `${r.displayName || r.emailAddress} <${r.emailAddress}>`)
              .join(", ");
            if (ccAddresses) headers.push(`Cc: ${ccAddresses}`);
          }

          // Add BCC recipients with type safety
          const bccRecipients = (item.bcc as unknown as Office.EmailAddressDetails[]) || [];
          if (bccRecipients.length > 0) {
            const bccAddresses = bccRecipients
              .filter((r) => r && r.emailAddress)
              .map((r) => `${r.displayName || r.emailAddress} <${r.emailAddress}>`)
              .join(", ");
            if (bccAddresses) headers.push(`Bcc: ${bccAddresses}`);
          }

          // Add other headers with proper fallbacks
          headers.push(`Subject: ${item.subject || "No Subject"}`);
          headers.push(`Date: ${item.dateTimeCreated?.toUTCString() || new Date().toUTCString()}`);
          headers.push(
            `Message-ID: <${item.internetMessageId || `${Date.now()}.${Math.random().toString(36).substring(2)}@outlook.office.com`}>`
          );
          headers.push("MIME-Version: 1.0");
          headers.push(`Content-Type: multipart/mixed; boundary="${boundary}"`);

          // Start constructing the email body
          let emailContent = headers.join("\r\n");
          emailContent += "\r\n\r\n";

          // Add the text body part
          emailContent += `--${boundary}\r\n`;
          emailContent += "Content-Type: text/plain; charset=utf-8\r\n";
          emailContent += "Content-Transfer-Encoding: 7bit\r\n\r\n";
          emailContent += result.value || "";
          emailContent += "\r\n\r\n";

          // Get HTML body if available
          try {
            const htmlBody = await new Promise<string>((resolveHtml, rejectHtml) => {
              Office.context.mailbox.item.body.getAsync(Office.CoercionType.Html, (htmlResult) => {
                if (htmlResult.status === Office.AsyncResultStatus.Succeeded && htmlResult.value) {
                  resolveHtml(htmlResult.value);
                } else {
                  rejectHtml(new Error("Failed to get HTML body"));
                }
              });
            });

            // Add HTML part if available
            emailContent += `--${boundary}\r\n`;
            emailContent += "Content-Type: text/html; charset=utf-8\r\n";
            emailContent += "Content-Transfer-Encoding: 7bit\r\n\r\n";
            emailContent += htmlBody;
            emailContent += "\r\n\r\n";
          } catch (htmlError) {
            console.warn("HTML body not available:", htmlError);
            // Continue without HTML part - not critical
          }

          // Add inline images and attachments if present
          if (item.attachments && item.attachments.length > 0) {
            for (const attachment of item.attachments) {
              if (!attachment.id) {
                console.warn(`Skipping attachment without ID: ${attachment.name}`);
                continue;
              }

              try {
                const content = await new Promise<string>((resolveAtt, rejectAtt) => {
                  Office.context.mailbox.item.getAttachmentContentAsync(attachment.id, (attResult) => {
                    if (attResult.status === Office.AsyncResultStatus.Succeeded && attResult.value?.content) {
                      resolveAtt(attResult.value.content);
                    } else {
                      rejectAtt(new Error(`Failed to get content for attachment: ${attachment.name}`));
                    }
                  });
                });

                // Validate content before adding
                if (!content) {
                  console.warn(`Empty content for attachment: ${attachment.name}`);
                  continue;
                }

                emailContent += `--${boundary}\r\n`;
                const ext = attachment.name ? attachment.name.split(".").pop()?.toLowerCase() : "";
                const mimeType = mimeTypes.get(ext || "") || "application/octet-stream";
                emailContent += `Content-Type: ${mimeType}; name="${attachment.name}"\r\n`;
                emailContent += "Content-Transfer-Encoding: base64\r\n";
                emailContent += `Content-Disposition: ${attachment.isInline ? "inline" : "attachment"}; filename="${attachment.name}"\r\n`;
                if (attachment.isInline) {
                  const cid = (attachment as any).contentId || attachment.name;
                  emailContent += `Content-ID: <${cid}>\r\n`;
                }
                emailContent += "\r\n";
                emailContent += content.replace(/([^\0]{76})/g, "$1\r\n");
                emailContent += "\r\n\r\n";
              } catch (attError) {
                console.warn(`Failed to process attachment ${attachment.name}:`, attError);
                // Continue with other attachments - don't fail the whole process
              }
            }
          }

          // Close the MIME message
          emailContent += `--${boundary}--\r\n`;

          resolve(emailContent);
        } else {
          reject(new Error(`Failed to get email content: ${result.error?.message || "Unknown error"}`));
        }
      });
    } catch (error) {
      reject(error instanceof Error ? error : new Error("Unknown error in email processing"));
    }
  });
};
// Modify getAttachmentContent function to handle the current email
/**
 * Retrieves the base64 encoded content of an attachment.
 * @param attachmentId The ID of the attachment.
 * @param isEml Whether the attachment is the generated EML file itself.
 * @returns A promise resolving to the base64 string.
 */
const getAttachmentContent = async (
  attachmentId: string,
  attachmentType: Office.MailboxEnums.AttachmentType
): Promise<{ content: string | ArrayBuffer | Uint8Array; contentType: string }> => {
  if (attachmentId === "current_email") {
    const emlContent = cachedEmlContent || (await getEmailAsEML());
    return {
      content: emlContent,
      contentType: "eml",
    };
  }

  return new Promise((resolve, reject) => {
    try {
      const item = Office.context.mailbox.item;
      if (!item) {
        reject(new Error("No valid email item is currently open."));
        return;
      }
      item.getAttachmentContentAsync(attachmentId, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          if (!result.value || !result.value.content) {
            reject(new Error("No valid content received for attachment."));
          } else {
            const format = result.value.format;
            let contentType: string;
            let contentValue: string | ArrayBuffer | Uint8Array;

            if (format === Office.MailboxEnums.AttachmentContentFormat.Base64) {
              contentType = "base64";
              contentValue = result.value.content;
            } else if (attachmentType === Office.MailboxEnums.AttachmentType.Item) {
              contentType = "eml";
              contentValue = result.value.content;
            } else if (format === Office.MailboxEnums.AttachmentContentFormat.Url) {
              console.warn(`Attachment ${attachmentId} is a URL type, content cannot be extracted directly.`);
              reject(new Error("URL attachments cannot be uploaded as file content."));
              return;
            } else {
              contentType = "binary";
              contentValue = result.value.content;
            }
            resolve({
              content: contentValue,
              contentType: contentType,
            });
          }
        } else {
          reject(new Error(`Failed to get attachment content: ${result.error?.message || "Unknown error"}`));
        }
      });
    } catch (error) {
      reject(error);
    }
  });
};
// Helper function to convert base64 string to ArrayBuffer using Fetch API (more robust)
/**
 * Converts a base64 string into an ArrayBuffer.
 * @param base64 The base64 encoded string.
 * @returns A promise resolving to the ArrayBuffer.
 */
const base64ToArrayBuffer = async (base64: string): Promise<ArrayBuffer> => {
  try {
    const response = await fetch(`data:application/octet-stream;base64,${base64}`);
    if (!response.ok) {
      throw new Error(`Failed to create blob from base64 string: HTTP status ${response.status}`);
    }
    const blob = await response.blob();
    return await blob.arrayBuffer();
  } catch (error) {
    console.error("Error converting base64 to ArrayBuffer:", error);
    throw new Error(
      `Failed to process the attachment. The file might be corrupted. Error: ${error instanceof Error ? error.message : String(error)}`
    );
  }
};
// Format file size - already robust
/**
 * Formats a file size in bytes into a human-readable string (KB, MB, GB).
 * @param bytes The size in bytes.
 * @returns The formatted size string.
 */
const formatFileSize = (bytes: number): string => {
  try {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  } catch (error) {
    console.error("Error formatting file size:", error);
    return "Unknown size";
  }
};
// Get file icon class - already robust
/**
 * Returns the appropriate FontAwesome icon class for a given file name based on its extension.
 * @param fileName The name of the file.
 * @returns The FontAwesome icon class string.
 */
const getFileIcon = (fileName: string): string => {
  try {
    const extension = fileName.split(".").pop()?.toLowerCase() || "";
    return fileIcons[extension] || "file";
  } catch (error) {
    console.error("Error getting file icon:", error);
    return "file";
  }
};

/************************************************************************
 * 7. UPLOAD & SAVE OPERATIONS
 ************************************************************************/

// Upload attachments to SharePoint with comprehensive error handling
/**
 * Triggers the upload process, initializing data if not yet done, and fetching custom columns.
 */
const uploadAttachments = async () => {
  try {
    if (!previewMode) {
      await previewAttachments(); // If not in preview mode, first preview them
      return;
    }

    const attachmentsToUpload = attachments.filter((att) => att.checked);

    if (attachmentsToUpload.length === 0) {
      showResult("No attachments selected for upload.", "info");
      return;
    }

    if (!hasCustomColumnsFetched) {
      showProcessingMessage("Loading library details...");
      customColumnsConfig = await fetchCustomColumns();
      hasCustomColumnsFetched = true;
      hideProcessingMessage();
    }

    if (customColumnsConfig.length > 0 && currentMetadataValues === null) {
      showMetadataModal();
      return;
    }

    await proceedWithUpload();
  } catch (error) {
    console.error("Unexpected error in upload function logic:", error);
    showResult(
      "An unexpected error occurred during upload. Please try again. (Overall upload function error)",
      "error"
    );
    resetAfterUpload();
  }
};
/**
 * Executes the actual upload of selected attachments and the EML file to SharePoint via Graph API.
 */
const proceedWithUpload = async () => {
  const attachmentsToUpload = attachments.filter((att) => att.checked);
  try {
    const accessToken = await getAccessToken();
    const folderName =
      selectedFolderCrumb?.name ??
      (breadcrumbHistory.length > 0 ? breadcrumbHistory[breadcrumbHistory.length - 1].name : "Root");

    showProcessingMessage(`Uploading ${attachmentsToUpload.length} file(s) to "${folderName}"...`);

    const uploadPromises = attachmentsToUpload.map(async (attachment) => {
      let fileNameToUpload = attachment.name;
      let contentBody: ArrayBuffer | Uint8Array;
      let contentTypeHeader = "application/octet-stream";

      try {
        const attachmentContent = await getAttachmentContent(
          attachment.id,
          attachment.attachmentType as Office.MailboxEnums.AttachmentType
        );

        if (attachmentContent.contentType === "base64") {
          if (typeof attachmentContent.content !== "string") {
            throw new Error(`Expected string content for base64 attachment, got ${typeof attachmentContent.content}`);
          }
          contentBody = await base64ToArrayBuffer(attachmentContent.content);
          const ext = fileNameToUpload.split(".").pop()?.toLowerCase();
          contentTypeHeader = mimeTypes.get(ext || "") || "application/octet-stream"; // ***OPTIMIZATION: Use Map for MIME types***
        } else if (attachmentContent.contentType === "eml") {
          if (typeof attachmentContent.content !== "string") {
            throw new Error(`Expected string content for EML attachment, got ${typeof attachmentContent.content}`);
          }
          const textEncoder = new TextEncoder();
          contentBody = textEncoder.encode(attachmentContent.content);
          fileNameToUpload = attachment.name.endsWith(".eml") ? attachment.name : `${attachment.name}.eml`;
          contentTypeHeader = "message/rfc822";
        } else if (attachmentContent.contentType === "binary") {
          if (attachmentContent.content instanceof ArrayBuffer) {
            contentBody = new Uint8Array(attachmentContent.content);
          } else if (attachmentContent.content instanceof Uint8Array) {
            contentBody = attachmentContent.content;
          } else if (typeof attachmentContent.content === "string") {
            contentBody = Uint8Array.from(attachmentContent.content, (c) => c.charCodeAt(0));
          } else {
            throw new Error(
              `Unsupported binary content format for ${attachment.name}: ${typeof attachmentContent.content}`
            );
          }
        } else {
          throw new Error(
            `Unsupported attachment content type: ${attachmentContent.contentType} for ${attachment.name}`
          );
        }

        const uploadUrl = `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${currentFolderId}:/${encodeURIComponent(fileNameToUpload)}:/content`;

        const response = await fetchWithRetry(uploadUrl, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": contentTypeHeader,
          },
          body: contentBody as BodyInit,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          let apiErrorMessage = errorData.error?.message || response.statusText || "Unknown API error";
          if (response.status === 409) {
            apiErrorMessage = `File with name '${fileNameToUpload}' already exists.`;
          }
          throw new Error(`HTTP ${response.status}: ${apiErrorMessage}`);
        }

        const newItem = await response.json();

        if (currentMetadataValues && Object.keys(currentMetadataValues).length > 0) {
          try {
            const patchUrl = `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${newItem.id}/listItem/fields`;
            await fetchWithRetry(patchUrl, {
              method: "PATCH",
              headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify(currentMetadataValues),
            });
          } catch (patchErr) {
            console.error(`Failed to update metadata for ${fileNameToUpload}:`, patchErr);
          }
        }

        console.log(`Successfully uploaded: ${fileNameToUpload}`);
        return { status: "fulfilled", fileName: fileNameToUpload };
      } catch (error) {
        console.error(`Error uploading ${attachment.name}:`, error);
        return {
          status: "rejected",
          fileName: attachment.name,
          error: error instanceof Error ? error.message : String(error),
        };
      }
    });

    const results = await Promise.allSettled(uploadPromises);

    let successCount = 0;
    let errorCount = 0;
    const errorMessages: string[] = [];
    const uploadedFileNames: string[] = [];

    results.forEach((result) => {
      if (result.status === "fulfilled") {
        const value = result.value as any;
        if (value.status === "fulfilled") {
          successCount++;
          uploadedFileNames.push(value.fileName);
        } else {
          errorCount++;
          errorMessages.push(`• ${value.fileName || "Unknown File"}: ${value.error || "Unknown error occurred."}`);
        }
      } else {
        errorCount++;
        const reason = result.reason as { fileName?: string; error?: string };
        errorMessages.push(`• ${reason?.fileName || "Unknown File"}: ${reason?.error || result.reason?.message || "Unknown error occurred."}`);
      }
    });

    if (uploadedFileNames.length > 0) {
      // Create a local ISO string without 'Z' so SharePoint stores and displays the exact local time
      const now = new Date();
      const localIsoString = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, -1);

      await logUploadDetailsToList(
        Office.context.mailbox.userProfile.displayName || "Unknown User",
        localIsoString,
        folderName,
        uploadedFileNames
      );
    }

    if (errorCount === 0) {
      showProcessingMessage(`Successfully uploaded ${successCount} file(s) to "${folderName}".`, false);
      await new Promise((res) => setTimeout(res, 2500));
    } else if (successCount > 0) {
      showProcessingMessage(`Uploaded ${successCount} file(s), ${errorCount} failed.`, false);
      await new Promise((res) => setTimeout(res, 3500));
      showResult(
        `Uploaded ${successCount} file(s), ${errorCount} failed.<br>Errors: ${errorMessages.join("<br>")}`,
        "warning"
      );
    } else {
      showProcessingMessage(`Failed to upload all files.`, false);
      await new Promise((res) => setTimeout(res, 3500));
      showResult(`Failed to upload all files.<br>Errors: ${errorMessages.join("<br>")}`, "error");
    }
  } catch (error) {
    console.error("Overall upload process initiation failed:", error);
    showResult(
      "Failed to start upload process. Please check your connection and permissions. (Upload initiation failed)",
      "error"
    );
  } finally {
    resetAfterUpload();
  }
};
/**
 * Logs the upload details (metadata) to a SharePoint tracking list if configured.
 * @param siteId The ID of the SharePoint site.
 * @param accessToken The MSAL access token.
 * @param fileName The name of the uploaded file.
 * @param folderPath The path where the file was saved.
 * @param itemUrl The direct URL to the uploaded item.
 */
const logUploadDetailsToList = async (
  userName: string,
  uploadDateTime: string,
  destinationFolderName: string,
  fileNames: string[]
) => {
  showProcessingMessage("Logging upload details...");
  try {
    const accessToken = await getAccessToken();

    const fileNameString = fileNames.join(", ");

    const listItemData = {
      fields: {
        Title: userName,
        UploadDateTime: uploadDateTime,
        DestinationFolder: destinationFolderName,
        FileName: fileNameString,
      },
    };

    const targetSiteId = await resolveNativeSiteId(sharePointLogSiteId, sharePointLogListId);
    console.log("Attempting to log to SharePoint with data:", {
      siteId: targetSiteId,
      listId: sharePointLogListId,
      data: listItemData,
    });

    const listUrl = `https://graph.microsoft.com/v1.0/sites/${targetSiteId}/lists/${sharePointLogListId}/items`;

    const response = await fetchWithRetry(listUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(listItemData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error?.message || response.statusText || "Unknown error";
      console.error("Error logging upload details to SharePoint list:", {
        status: response.status,
        statusText: response.statusText,
        errorData: errorData,
        requestUrl: listUrl,
        requestBody: listItemData,
      });
      throw new Error(`SharePoint logging failed: ${errorMessage} (Status: ${response.status})`);
    } else {
      const responseData = await response.json();
      console.log("Upload details logged successfully:", responseData);
    }
  } catch (error) {
    console.error("Detailed error in logUploadDetailsToList:", error);
    const errorMessage = error instanceof Error ? error.message : String(error);
    showResult(`Upload completed but logging failed. Error: ${errorMessage}`, "warning");
  } finally {
    hideProcessingMessage();
  }
};
// Create new folder with error handling - already robust
/**
 * Creates a new subfolder in the currently selected SharePoint location.
 */
const createFolder = async () => {
  showProcessingMessage("Creating folder...");
  try {
    const folderNameInput = getHTMLElement("create-folder-name") as HTMLInputElement;
    const folderName = folderNameInput?.value.trim() || "";

    if (!folderName) {
      showCreateFolderMessage("Please enter a folder name", "error");
      return;
    }

    // Remember the parent folder before creating.
    // IMPORTANT: currentFolderId may point to a single-clicked (selected) folder that is NOT yet
    // in breadcrumbHistory — it only lives in selectedFolderCrumb. So prefer selectedFolderCrumb.name
    // as the parent name, falling back to the last folder crumb in history.
    const parentFolderId = currentFolderId;
    const parentFolderName =
      selectedFolderCrumb?.name || breadcrumbHistory.filter((c) => c.type === "folder").slice(-1)[0]?.name || "Root";

    try {
      const accessToken = await getAccessToken();
      /**
 * Creates a new subfolder in the currently selected SharePoint location.
 */
const createFolderResponse = await fetchWithRetry(
        `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${currentFolderId}/children`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: folderName,
            folder: {},
            "@microsoft.graph.conflictBehavior": "rename", // Option to rename if exists
          }),
        }
      );

      if (createFolderResponse.ok) {
        const newFolder = await createFolderResponse.json();

        showCreateFolderMessage(`Folder "${newFolder.name}" created successfully`, "success");
        if (folderNameInput) folderNameInput.value = "";

        /**
 * Creates a new subfolder in the currently selected SharePoint location.
 */
const createFolderInputContainer = getHTMLElement("create-folder-input-container");
        if (createFolderInputContainer) {
          createFolderInputContainer.classList.add("hidden");
          /**
 * Creates a new subfolder in the currently selected SharePoint location.
 */
const createFolderIcon = getHTMLElement("create-folder-icon");
          if (createFolderIcon) createFolderIcon.className = "fas fa-chevron-right";
        }

        // Step 1: Commit selectedFolderCrumb to history ONLY if it was pending (single-click).
        // If the user double-clicked into the parent folder, it is already in breadcrumbHistory
        // with the full correct path — we must NOT overwrite it.
        if (selectedFolderCrumb) {
          // Parent was only single-clicked, not in history yet — commit it.
          const lastFolderIdx = breadcrumbHistory.map((c) => c.type).lastIndexOf("folder");
          if (lastFolderIdx >= 0) {
            breadcrumbHistory = breadcrumbHistory.slice(0, lastFolderIdx + 1);
            breadcrumbHistory.push({ id: parentFolderId, name: parentFolderName, type: "folder" });
          }
        }
        selectedFolderCrumb = null;

        // Step 2: Select the new folder WITHOUT navigating into it.
        // selectFolder() sets currentFolderId and adds newFolder as a virtual trailing crumb
        // (selectedFolderCrumb). updateBreadcrumb() renders:
        //   … > Library > ParentFolder > NewFolder   ← NewFolder always after its parent ✓
        selectFolder(newFolder.id, newFolder.name);

        // Step 3: Reload the PARENT folder listing so the new folder row appears (highlighted).
        loadFolders(parentFolderId, parentFolderName);
      } else {
        const errorData = await createFolderResponse.json().catch(() => ({}));
        let errorMessage =
          errorData.error?.message || createFolderResponse.statusText || "Error creating folder. Please try again.";
        if (createFolderResponse.status === 409) {
          errorMessage = `A folder named '${folderName}' already exists.`;
          showCreateFolderMessage(errorMessage, "warning");
        } else {
          showCreateFolderMessage(errorMessage, "error");
        }
      }
    } catch (error) {
      console.error("Error creating folder network/API:", error);
      showCreateFolderMessage("Failed to create folder. You might not have permission.", "error");
    }
  } catch (error) {
    console.error("Error in create folder process logic:", error);
    showCreateFolderMessage("Something went wrong while creating the folder.", "error");
  } finally {
    hideProcessingMessage();
  }
};

/************************************************************************
 * 8. UI UPDATES & DOM MANIPULATION
 ************************************************************************/

/**
 * Helper function to safely get an HTML element by ID.
 * @param id The ID of the HTML element.
 * @returns The HTMLElement or null.
 */
// Safe DOM element access with null check - already robust
function getHTMLElement(id: string): HTMLElement | null {
  const element = document.getElementById(id);
  return element instanceof HTMLElement ? element : null;
}
/**
 * Clears the folder search input field and resets the folder list.
 */
const clearSearchInput = () => {
  const searchInput = getHTMLElement("folder-search") as HTMLInputElement;
  if (searchInput) searchInput.value = "";
};
// --- Processing Message Functions ---
/**
 * Displays a processing overlay with an optional spinner.
 * @param message The message to display.
 * @param showSpinner Whether to show a loading spinner.
 */
const showProcessingMessage = (message: string, showSpinner: boolean = true) => {
  try {
    const processingMessageContainer = getHTMLElement("processing-message-container");
    if (!processingMessageContainer) {
      console.warn("Processing message container not found.");
      return;
    }

    processingMessageContainer.innerHTML = `
        ${showSpinner ? '<i class="fas fa-spinner fa-spin processing-spinner"></i>' : ""}
        <span>${message}</span>
    `;
    processingMessageContainer.classList.remove("hidden");
  } catch (error) {
    console.error("Error showing processing message:", error);
  }
};
/**
 * Hides the processing overlay.
 */
const hideProcessingMessage = () => {
  try {
    const processingMessageContainer = getHTMLElement("processing-message-container");
    if (processingMessageContainer) {
      processingMessageContainer.classList.add("hidden");
      processingMessageContainer.innerHTML = "";
    }
  } catch (error) {
    console.error("Error hiding processing message:", error);
  }
};
// Show result message with enhanced styling - enhanced DOM safety
/**
 * Displays a status message (success, error, info) in the result container.
 * @param message The message text.
 * @param type The type of message (determines color styling).
 */
const showResult = (message: string, type: "success" | "error" | "info" | "warning" = "success") => {
  try {
    const resultContainer = getHTMLElement("result-container");
    if (!resultContainer) {
      console.warn("Result container not found.");
      return;
    }

    // Clear previous timeout if it's not an upload status message
    if (resultContainer.dataset.timeoutId && resultContainer.dataset.isUploadStatus !== "true") {
      clearTimeout(parseInt(resultContainer.dataset.timeoutId));
      delete resultContainer.dataset.timeoutId; // Clear the dataset property
    }

    resultContainer.innerHTML = message;
    resultContainer.className = `result-container ${type}`;
    resultContainer.dataset.isUploadStatus = "false"; // Mark as not a persistent upload status

    const timeoutId = setTimeout(() => {
      if (resultContainer.dataset.isUploadStatus !== "true") {
        resultContainer.textContent = "";
        resultContainer.className = "result-container";
        delete resultContainer.dataset.timeoutId;
        delete resultContainer.dataset.isUploadStatus;
      }
    }, 5000) as unknown as number;
    resultContainer.dataset.timeoutId = timeoutId.toString();
  } catch (error) {
    console.error("Error showing result message:", error);
  }
};
// Show persistent upload status message - enhanced DOM safety
/**
 * Displays an upload progress or status message in the main result container.
 * @param message The status message text.
 * @param type The severity or type of the message.
 */
const showUploadStatus = (message: string, type: "info" | "warning" | "error" | "success" = "info") => {
  try {
    const resultContainer = getHTMLElement("result-container");
    if (!resultContainer) {
      console.warn("Result container not found for upload status.");
      return;
    }

    // Clear any previous timeout if it exists
    if (resultContainer.dataset.timeoutId) {
      clearTimeout(parseInt(resultContainer.dataset.timeoutId));
      delete resultContainer.dataset.timeoutId;
    }

    resultContainer.innerHTML = message;
    resultContainer.className = `result-container ${type}`;
    resultContainer.dataset.isUploadStatus = "true"; // Mark as persistent upload status

    // Clear previous upload status timeout if it exists
    if (uploadStatusTimeout) {
      clearTimeout(uploadStatusTimeout);
      uploadStatusTimeout = null;
    }
  } catch (error) {
    console.error("Error showing upload status message:", error);
  }
};
// Clear upload status message - enhanced DOM safety
/**
 * Clears any currently displayed upload status messages.
 */
const clearUploadStatus = () => {
  try {
    const resultContainer = getHTMLElement("result-container");
    if (resultContainer) {
      // Only clear if it's currently showing an upload status
      if (resultContainer.dataset.isUploadStatus === "true") {
        resultContainer.textContent = "";
        resultContainer.className = "result-container";
        delete resultContainer.dataset.isUploadStatus;
      }
      if (uploadStatusTimeout) {
        clearTimeout(uploadStatusTimeout);
        uploadStatusTimeout = null;
      }
    }
  } catch (error) {
    console.error("Error clearing upload status message:", error);
  }
};
// Update folder table with search results - enhanced DOM safety and batching
/**
 * Updates the folder table UI with the fetched folder items.
 * @param items Array of folder objects to display.
 * @param parentId The ID of the parent folder.
 */
const updateFolderTable = (items: any[], parentId: string) => {
  try {
    const tableBody = document.querySelector("#folder-table tbody");
    if (!tableBody) return;
    tableBody.innerHTML = "";
    lastSelectedFolderRow = null;

    const fragment = document.createDocumentFragment();

    const noSubfolderMsg = getHTMLElement("no-subfolder-message");
    const searchInput = getHTMLElement("folder-search") as HTMLInputElement;
    const isSearching = searchInput?.value.trim().length > 0;

    if (noSubfolderMsg) {
      if (messageTimeout) clearTimeout(messageTimeout);
      if (items.length === 0) {
        noSubfolderMsg.classList.remove("hidden");

        let emptyText = "No items available";
        if (selectionMode === "site") emptyText = "No sites available";
        else if (selectionMode === "library") emptyText = "No libraries available";
        else if (selectionMode === "folder") {
          if (parentId === "root") emptyText = "No folder available";
          else emptyText = "No sub-folder available";
        }

        noSubfolderMsg.innerHTML = isSearching
          ? `<i class="fas fa-search"></i> No items match "${searchInput?.value || ""}"`
          : `<i class="fas fa-folder-open"></i> ${emptyText}`;
        messageTimeout = setTimeout(() => {
          if (noSubfolderMsg) noSubfolderMsg.classList.add("hidden");
          updateSelectionBodyVisibility(); // Update visibility when message disappears
        }, 3000) as unknown as number;
      } else {
        noSubfolderMsg.classList.add("hidden");
      }
    }
    updateSelectionBodyVisibility();

    items.forEach((item) => {
      const row = document.createElement("tr");
      const nameCell = document.createElement("td");
      nameCell.className = "folder-name";

      let icon = "fa-folder";
      if (selectionMode === "site") icon = "fa-globe";
      else if (selectionMode === "library") icon = "fa-book";

      const displayName = item.name || item.displayName || "Unknown";

      nameCell.innerHTML = `<i class="fas ${icon} folder-icon"></i> ${displayName}`;
      nameCell.style.cursor = "pointer";

      // Re-apply selection styling based on the active mode
      const isSelectedSite = selectionMode === "site" && item.id === currentSiteId;
      const isSelectedLibrary = selectionMode === "library" && item.id === currentLibraryId;
      const isSelectedFolder = selectionMode === "folder" && item.id === currentFolderId;

      if (isSelectedSite || isSelectedLibrary || isSelectedFolder) {
        row.classList.add("selected-folder-row");
        lastSelectedFolderRow = row;
      }

      nameCell.onclick = async (event) => {
        if (event.detail === 1) {
          // Remove previous selection styling
          if (lastSelectedFolderRow) lastSelectedFolderRow.classList.remove("selected-folder-row");
          row.classList.add("selected-folder-row");
          lastSelectedFolderRow = row;

          if (selectionMode === "site") {
            clearSearchInput();
            hasCustomColumnsFetched = false;
            currentMetadataValues = null;
            customColumnsConfig = [];
            currentSiteId = item.id;
            currentSiteName = displayName;
            selectionMode = "library";

            const libs = await fetchLibraries(item.id);
            updateFolderTable(libs, item.id);

            breadcrumbHistory = [{ id: item.id, name: displayName, type: "site" }];
            updateBreadcrumb();
            showChangeSiteLink(true);
            updateDynamicUIText();
          } else if (selectionMode === "library") {
            const hasEdit = await checkLibraryPermissions(item.id);
            if (!hasEdit) {
              showResult("You don't have the Edit Access.", "error");
              return;
            }
            clearSearchInput();
            hasCustomColumnsFetched = false;
            currentMetadataValues = null;
            customColumnsConfig = [];
            currentLibraryId = item.id;
            currentLibraryName = displayName;
            driveId = item.id;
            selectionMode = "folder";

            breadcrumbHistory = [
              { id: currentSiteId, name: currentSiteName, type: "site" },
              { id: currentLibraryId, name: currentLibraryName, type: "library" },
              { id: "root", name: "Root", type: "folder" },
            ];
            currentFolderId = "root";
            updateBreadcrumb();
            updateSelectionDisplay("Root");
            showChangeSiteLink(true);
            updateUploadButtonState();
            loadFolders("root", "Root");
          } else {
            selectFolder(item.id, displayName);
          }
        }
      };

      nameCell.ondblclick = async () => {
        if (selectionMode === "folder") {
          // Folder double click logic
          const subfolders = await fetchFolders(item.id);
          if (subfolders.length === 0) {
            selectFolder(item.id, displayName);
            if (lastSelectedFolderRow) lastSelectedFolderRow.classList.remove("selected-folder-row");
            row.classList.add("selected-folder-row");
            lastSelectedFolderRow = row;

            // Flash the empty message without navigating inside
            const noSubfolderMsg = getHTMLElement("no-subfolder-message");
            if (noSubfolderMsg) {
              if (messageTimeout) clearTimeout(messageTimeout);
              noSubfolderMsg.innerHTML = `<i class="fas fa-folder-open"></i> No sub-folder available`;
              noSubfolderMsg.classList.remove("hidden");
              messageTimeout = setTimeout(() => {
                if (noSubfolderMsg) noSubfolderMsg.classList.add("hidden");
              }, 3000) as unknown as number;
            }
          } else {
            navigateToFolder(item.id, displayName);
          }
        }
      };

      row.append(nameCell);
      fragment.appendChild(row);
    });

    tableBody.appendChild(fragment);
  } catch (error) {
    console.error("Error updating folder table:", error);
    showResult("Error displaying items. Please try again.", "error");
  }
};
/**
 * Updates the display text indicating the currently selected site, library, or folder.
 * @param selectedFolderName The name of the currently selected folder.
 */
const updateSelectionDisplay = (selectedFolderName: string = "Root") => {
  const container = getHTMLElement("selected-folder-container");
  const displayText = getHTMLElement("selection-display-text");
  /**
 * Creates a new subfolder in the currently selected SharePoint location.
 */
const createFolderSection = getHTMLElement("create-folder-section-container");

  if (container && displayText) {
    if (!currentSiteId && !currentLibraryId && selectionMode === "site") {
      container.classList.add("hidden");
    } else {
      container.classList.remove("hidden");
      let parts = [];
      if (currentSiteId) parts.push(`<div style="margin-bottom: 4px;"><b>Site:</b> ${currentSiteName}</div>`);
      if (currentLibraryId) parts.push(`<div style="margin-bottom: 4px;"><b>Library:</b> ${currentLibraryName}</div>`);
      if (selectionMode === "folder") parts.push(`<div><b>Folder:</b> ${selectedFolderName}</div>`);
      displayText.innerHTML = parts.join("");
    }
  }

  updateDynamicUIText();

  // Toggle Create Folder visibility
  if (createFolderSection) {
    if (selectionMode === "folder") {
      createFolderSection.classList.remove("hidden");
    } else {
      createFolderSection.classList.add("hidden");
      // Also hide the input container if it's open
      /**
 * Creates a new subfolder in the currently selected SharePoint location.
 */
const createFolderInputContainer = getHTMLElement("create-folder-input-container");
      if (createFolderInputContainer) {
        createFolderInputContainer.classList.add("hidden");
        /**
 * Creates a new subfolder in the currently selected SharePoint location.
 */
const createFolderIcon = getHTMLElement("create-folder-icon");
        if (createFolderIcon) createFolderIcon.className = "fas fa-chevron-right";
      }
    }
  }

  updateSelectionBodyVisibility();
};
/**
 * Toggles the visibility of the folder selection body based on the current selection mode.
 */
const updateSelectionBodyVisibility = () => {
  const selectionBody = getHTMLElement("folder-selection-body");
  if (!selectionBody) return;

  const noSubfolderMsg = getHTMLElement("no-subfolder-message");
  const selectedFolderContainer = getHTMLElement("selected-folder-container");
  const changeSiteLink = getHTMLElement("change-site-link");
  const saveFavContainer = getHTMLElement("save-favorite-container");
  const changeSiteLinkContainer = getHTMLElement("change-site-link-container");

  const isNoSubfolderVisible = noSubfolderMsg && !noSubfolderMsg.classList.contains("hidden");
  const isSelectedFolderVisible = selectedFolderContainer && !selectedFolderContainer.classList.contains("hidden");
  const isChangeSiteLinkVisible = changeSiteLink && !changeSiteLink.classList.contains("hidden");
  const isSaveFavVisible = saveFavContainer && !saveFavContainer.classList.contains("hidden");

  // The parent container for change site/save favorite should be visible if either child is visible
  if (changeSiteLinkContainer) {
    if (isChangeSiteLinkVisible || isSaveFavVisible) {
      changeSiteLinkContainer.classList.remove("hidden");
    } else {
      changeSiteLinkContainer.classList.add("hidden");
    }
  }

  if (isNoSubfolderVisible || isSelectedFolderVisible || isChangeSiteLinkVisible || isSaveFavVisible) {
    selectionBody.classList.remove("hidden");
    const selectionIcon = getHTMLElement("folder-selection-icon");
    if (selectionIcon && selectionIcon.style.display !== "none") {
      selectionIcon.className = "fas fa-chevron-down";
    }
  } else {
    selectionBody.classList.add("hidden");
    const selectionIcon = getHTMLElement("folder-selection-icon");
    if (selectionIcon && selectionIcon.style.display !== "none") {
      selectionIcon.className = "fas fa-chevron-right";
    }
  }
};
/**
 * Updates UI labels dynamically based on whether the user is selecting a site, library, or folder.
 */
const updateDynamicUIText = () => {
  // 1. Update Header
  const header = getHTMLElement("selection-header");
  const selectionIcon = getHTMLElement("folder-selection-icon");

  if (header) {
    if (!hasInitializedData)
      header.textContent = "Folder Selection"; // Using Folder Selection as default instead of Welcome as it looks better
    else if (selectionMode === "site") header.textContent = "Site Selection";
    else if (selectionMode === "library") header.textContent = "Library Selection";
    else header.textContent = "Folder Selection";
  }

  if (selectionIcon) {
    if (selectionMode === "site") {
      selectionIcon.style.display = "none";
    } else {
      selectionIcon.style.display = "";
    }
  }

  const breadcrumbRow = getHTMLElement("breadcrumb-row");
  if (breadcrumbRow) {
    if (selectionMode === "site") {
      breadcrumbRow.style.display = "none";
    } else {
      breadcrumbRow.style.display = "";
    }
  }

  // 2. Update Folder Name Header Text
  const folderNameHeader = getHTMLElement("folder-name-header");
  if (folderNameHeader) {
    if (selectionMode === "site") folderNameHeader.textContent = "Site name";
    else if (selectionMode === "library") folderNameHeader.textContent = "Library name";
    else folderNameHeader.textContent = "Folder name";
  }

  // 3. Update Search Placeholder
  const searchInput = getHTMLElement("folder-search") as HTMLInputElement;
  if (searchInput) {
    if (selectionMode === "site") searchInput.placeholder = "Search sites...";
    else if (selectionMode === "library") searchInput.placeholder = "Search libraries...";
    else searchInput.placeholder = "Search folders...";
  }

  // 4. Update Save Favorite Visibility
  const saveFavContainer = getHTMLElement("save-favorite-container");
  const saveFavCb = getHTMLElement("save-favorite-checkbox") as HTMLInputElement;
  const isChecked = saveFavCb && saveFavCb.checked;
  if (saveFavContainer) {
    if (selectionMode === "folder" || isChecked) {
      saveFavContainer.classList.remove("hidden");
    } else {
      saveFavContainer.classList.add("hidden");
    }
  }

  // 5. Update Table Header
  const tableHeader = document.querySelector("#folder-table th.folder-name") as HTMLElement;
  if (tableHeader) {
    if (selectionMode === "site") tableHeader.textContent = "Site Name";
    else if (selectionMode === "library") tableHeader.textContent = "Library Name";
    else tableHeader.textContent = "Folder Name";
  }

  updateSelectionBodyVisibility();
};
// Function to update the upload button's enabled/disabled state - enhanced DOM safety
/**
 * Enables or disables the upload button based on the current selection and initialization state.
 */
const updateUploadButtonState = () => {
  try {
    const selectedAttachments = attachments.filter((att) => att.checked);
    const uploadBtn = getHTMLElement("upload-btn") as HTMLButtonElement;

    if (uploadBtn) {
      if (!hasInitializedData) {
        // Add-in not started yet — show neutral enabled-looking text
        uploadBtn.disabled = true;
        uploadBtn.textContent = `View Attachments (v${APP_VERSION})`;
      } else if (selectionMode === "site") {
        // No site selected yet — nothing can be uploaded
        uploadBtn.disabled = true;
        uploadBtn.textContent = `Select a Site and Library (v${APP_VERSION})`;
      } else if (selectionMode === "library") {
        // Site chosen but no library selected yet
        uploadBtn.disabled = true;
        uploadBtn.textContent = `Select a Site and Library (v${APP_VERSION})`;
      } else if (previewMode && selectedAttachments.length > 0) {
        // Library selected, in preview mode, attachments checked → ready to upload
        uploadBtn.disabled = false;
        uploadBtn.textContent = `Upload ${selectedAttachments.length} Selected File(s) to SharePoint (v${APP_VERSION})`;
      } else if (previewMode) {
        // Library selected, in preview mode, but no attachments checked
        uploadBtn.disabled = true;
        uploadBtn.textContent = `No Attachments Selected (v${APP_VERSION})`;
      } else {
        // Library selected (favorite or manual), at Root or any folder → ready to view attachments
        uploadBtn.disabled = false;
        uploadBtn.textContent = `View Attachments (v${APP_VERSION})`;
      }
    } else {
      console.warn("Upload button not found, cannot update its state.");
    }
  } catch (error) {
    console.error("Error updating upload button state:", error);
    showResult("Error updating button state. Please refresh.", "error");
  }
};
/**
 * Resets the UI and state variables after a successful upload operation.
 */
const resetAfterUpload = () => {
  try {
    const selectionBody = getHTMLElement("folder-selection-body");
    if (selectionBody) selectionBody.classList.add("hidden");

    cachedEmlContent = null;

    hasCustomColumnsFetched = false;
    currentMetadataValues = null;
    customColumnsConfig = [];

    // --- Reset folder/breadcrumb state based on whether a favorite is saved ---
    selectedFolderCrumb = null;

    if (currentSiteId && currentLibraryId) {
      // Favorite exists → restore to Site > Library > Root view
      selectionMode = "folder";
      currentFolderId = "root";
      breadcrumbHistory = [
        { id: currentSiteId, name: currentSiteName, type: "site" },
        { id: currentLibraryId, name: currentLibraryName, type: "library" },
        { id: "root", name: "Root", type: "folder" },
      ];
      updateBreadcrumb();
      updateSelectionDisplay("Root");
    } else {
      // No favorite → show site selection from scratch
      selectionMode = "site";
      currentSiteId = "";
      currentSiteName = "";
      currentLibraryId = "";
      currentLibraryName = "";
      driveId = "";
      currentFolderId = "root";
      breadcrumbHistory = [];
      updateBreadcrumb();
      updateSelectionDisplay();
      showChangeSiteLink(false);
      showSaveFavorite(true);
      const h3 = getHTMLElement("selection-header");
      if (h3) h3.textContent = "Site and Library Selection";
    }

    // --- Reset attachments ---
    attachments = [];
    const attachmentsTableBody = document.querySelector("#attachments-preview tbody");
    if (attachmentsTableBody) attachmentsTableBody.innerHTML = "";

    const noAttachmentsMsg = getHTMLElement("no-attachments-message");
    const attachmentsPreviewContainer = getHTMLElement("attachments-preview");
    const selectAllCheckbox = getHTMLElement("select-all-attachments") as HTMLInputElement;

    if (noAttachmentsMsg) noAttachmentsMsg.classList.remove("hidden");
    if (attachmentsPreviewContainer) attachmentsPreviewContainer.classList.add("hidden");
    if (selectAllCheckbox) {
      selectAllCheckbox.checked = false;
      selectAllCheckbox.disabled = true;
    }

    const selectedFolderNameEl = getHTMLElement("selected-folder-name");
    if (selectedFolderNameEl) {
      selectedFolderNameEl.textContent = "Root";
      selectedFolderNameEl.dataset.folderId = "root";
    }

    clearSearchInput();

    previewMode = false;
    updateUploadButtonState();

    /**
 * Creates a new subfolder in the currently selected SharePoint location.
 */
const createFolderInputContainer = getHTMLElement("create-folder-input-container");
    /**
 * Creates a new subfolder in the currently selected SharePoint location.
 */
const createFolderNameInput = getHTMLElement("create-folder-name") as HTMLInputElement;
    if (createFolderInputContainer) {
      createFolderInputContainer.classList.add("hidden");
      /**
 * Creates a new subfolder in the currently selected SharePoint location.
 */
const createFolderIcon = getHTMLElement("create-folder-icon");
      if (createFolderIcon) createFolderIcon.className = "fas fa-chevron-right";
    }
    if (createFolderNameInput) createFolderNameInput.value = "";

    const noSubfolderMsg = getHTMLElement("no-subfolder-message");
    if (noSubfolderMsg) {
      if (messageTimeout) {
        clearTimeout(messageTimeout);
        messageTimeout = null;
      }
      noSubfolderMsg.classList.add("hidden");
    }

    clearUploadStatus();
    hideProcessingMessage();

    // --- Reload folder listing if the folder panel is visible ---
    const folderContainer = getHTMLElement("folder-container");
    if (folderContainer && !folderContainer.classList.contains("hidden")) {
      if (currentSiteId && currentLibraryId) {
        loadFolders("root", "Root");
      } else {
        fetchSites().then((sites) => updateFolderTable(sites, "root"));
      }
    }
  } catch (error) {
    console.error("Error resetting after upload:", error);
    showResult("Error resetting interface. Please refresh the add-in.", "error");
  }
};
// Render contextual notices inside the top folder banner instead of the bottom result log
/**
 * Displays a top banner message (e.g., version notifications) dynamically.
 * @param html The HTML content of the banner.
 */
const showTopBannerMessage = (html: string) => {
  const noSubfolderMsg = getHTMLElement("no-subfolder-message");
  if (noSubfolderMsg) {
    if (messageTimeout) clearTimeout(messageTimeout);
    noSubfolderMsg.innerHTML = html;
    noSubfolderMsg.classList.remove("hidden");
    messageTimeout = setTimeout(() => {
      if (noSubfolderMsg) noSubfolderMsg.classList.add("hidden");
    }, 5000) as unknown as number;
  }
};
/**
 * Displays a temporary status message in the create folder section.
 * @param message The message text.
 * @param type The type of message (success, error, warning).
 */
const showCreateFolderMessage = (message: string, type: "success" | "error" | "warning" = "success") => {
  const messageDiv = getHTMLElement("create-folder-message");
  if (!messageDiv) return;

  if (createFolderMessageTimeout) {
    clearTimeout(createFolderMessageTimeout);
  }

  let bgColor = "";
  let textColor = "";
  let iconClass = "";

  if (type === "success") {
    bgColor = "#dff6dd";
    textColor = "#107c10";
    iconClass = "fas fa-check-circle";
  } else if (type === "error") {
    bgColor = "#fde7e9";
    textColor = "#d13438";
    iconClass = "fas fa-times-circle";
  } else if (type === "warning") {
    bgColor = "#fff4ce";
    textColor = "#797775";
    iconClass = "fas fa-exclamation-triangle";
  }

  messageDiv.style.backgroundColor = bgColor;
  messageDiv.style.color = textColor;
  messageDiv.innerHTML = `<i class="${iconClass}"></i> <span>${message}</span>`;
  messageDiv.classList.remove("hidden");

  createFolderMessageTimeout = window.setTimeout(() => {
    messageDiv.classList.add("hidden");
  }, 5000);
};

/************************************************************************
 * 9. ATTACHMENTS UI & INTERACTION
 ************************************************************************/

// Function to re-render attachments table (called after selection changes) - enhanced DOM safety and batching
/**
 * Renders the attachments table UI with checkboxes for selection.
 */
const renderAttachmentsTable = () => {
  try {
    const tableBody = document.querySelector("#attachments-preview tbody");
    if (!tableBody) {
      console.warn("Attachments preview table body not found.");
      return;
    }
    tableBody.innerHTML = "";

    const selectAllCheckbox = getHTMLElement("select-all-attachments") as HTMLInputElement;
    if (selectAllCheckbox) {
      selectAllCheckbox.checked = attachments.length > 0 && attachments.every((att) => att.checked);
      // Disable selectAllCheckbox if no attachments, enable if there are
      selectAllCheckbox.disabled = attachments.length === 0;
    }

    const fragment = document.createDocumentFragment(); // ***OPTIMIZATION: Create a document fragment***

    attachments.forEach((attachment, index) => {
      const row = document.createElement("tr");
      row.className = attachment.checked ? "selected-attachment-row" : "";

      const checkboxCell = document.createElement("td");
      const checkbox = document.createElement("input");
      checkbox.type = "checkbox";
      checkbox.checked = attachment.checked;
      checkbox.dataset.index = index.toString();
      checkbox.addEventListener("change", (e) => {
        try {
          const changedCheckbox = e.target as HTMLInputElement;
          if (changedCheckbox) {
            attachment.checked = changedCheckbox.checked;
            row.className = attachment.checked ? "selected-attachment-row" : "";
            updateUploadButtonState();
            if (selectAllCheckbox) {
              selectAllCheckbox.checked = attachments.every((att) => att.checked);
            }
          }
        } catch (innerError) {
          console.error("Error handling attachment checkbox change:", innerError);
          showResult("An error occurred with an attachment checkbox.", "error");
        }
      });
      checkboxCell.appendChild(checkbox);

      const nameCell = document.createElement("td");
      nameCell.className = "file-name";
      nameCell.innerHTML = `<i class="fas fa-${getFileIcon(attachment.name)} file-icon"></i> ${attachment.name}`;

      const typeCell = document.createElement("td");
      typeCell.className = "file-type";
      let attachmentType = attachment.name.split(".").pop()?.toUpperCase() || "FILE";
      if (attachment.attachmentType === Office.MailboxEnums.AttachmentType.Item) {
        attachmentType = "EML";
      }
      typeCell.textContent = attachmentType;

      const sizeCell = document.createElement("td");
      sizeCell.className = "file-size";
      const fileSize = attachment.size || 0;
      sizeCell.textContent = fileSize === 0 ? "N/A" : formatFileSize(fileSize);

      row.append(checkboxCell, nameCell, typeCell, sizeCell);
      fragment.appendChild(row); // Append to the fragment
    });

    tableBody.appendChild(fragment); // ***OPTIMIZATION: Append fragment to the table body once***
  } catch (error) {
    console.error("Error rendering attachments table:", error);
    showResult("Error displaying attachments. Please try again.", "error");
  }
};
// Function to handle 'select all' checkbox - enhanced DOM safety
/**
 * Toggles the selection state of all attachments in the list.
 * @param event The click event on the select-all checkbox.
 */
const toggleSelectAllAttachments = (event: Event) => {
  try {
    const target = event.target as HTMLInputElement;
    if (!target) {
      console.warn("Event target is null in toggleSelectAllAttachments.");
      return;
    }
    const isChecked = target.checked;
    attachments.forEach((att) => (att.checked = isChecked));
    renderAttachmentsTable();
    updateUploadButtonState();
  } catch (error) {
    console.error("Error toggling select all attachments:", error);
    showResult("Error with select all functionality.", "error");
  }
};
// Preview attachments with comprehensive error handling - already robust
/**
 * Loads attachments from the current Outlook item and generates the EML file preview.
 */
const previewAttachments = async () => {
  showProcessingMessage("Loading attachments...");
  try {
    const item = Office.context.mailbox.item;
    if (!item) {
      showResult("Could not access the current email item.", "error");
      return;
    }

    const noAttachmentsMsg = getHTMLElement("no-attachments-message");
    const attachmentsPreviewContainer = getHTMLElement("attachments-preview");
    const selectAllCheckbox = getHTMLElement("select-all-attachments") as HTMLInputElement;

    // Reset attachments array
    attachments = [];

    // Add the current email as an .eml file
    const subject = item.subject || "Untitled";
    const sanitizedSubject = subject.replace(/[<>:"/\\|?*]/g, "_"); // Remove invalid file characters
    const emailFileName = `${sanitizedSubject}.eml`;

    cachedEmlContent = null;
    let emlSize = 0;
    try {
      cachedEmlContent = await getEmailAsEML();
      emlSize = new Blob([cachedEmlContent]).size;
    } catch (e) {
      console.warn("Could not pre-generate EML size:", e);
    }

    attachments.push({
      id: "current_email",
      name: emailFileName,
      size: emlSize,
      attachmentType: Office.MailboxEnums.AttachmentType.Item,
      isInline: false,
      checked: true,
      contentType: "message/rfc822",
      contentId: "",
    } as any);

    // Add regular attachments
    if (item.attachments && item.attachments.length > 0) {
      const relevantAttachments = item.attachments.filter(
        (att) =>
          att.isInline === false &&
          (att.attachmentType === Office.MailboxEnums.AttachmentType.File ||
            att.attachmentType === Office.MailboxEnums.AttachmentType.Item)
      );

      for (let i = 0; i < relevantAttachments.length; i++) {
        const attachment = relevantAttachments[i];
        attachments.push({ ...attachment, checked: true });
      }
    }

    if (attachments.length === 0) {
      if (noAttachmentsMsg) noAttachmentsMsg.classList.remove("hidden");
      if (attachmentsPreviewContainer) attachmentsPreviewContainer.classList.add("hidden");
      if (selectAllCheckbox) selectAllCheckbox.disabled = true;

      showResult("No attachments or email content to upload.", "info");
      updateUploadButtonState();
      return;
    }

    if (noAttachmentsMsg) noAttachmentsMsg.classList.add("hidden");
    if (attachmentsPreviewContainer) attachmentsPreviewContainer.classList.remove("hidden");
    if (selectAllCheckbox) selectAllCheckbox.disabled = false;

    renderAttachmentsTable();
    previewMode = true;
    updateUploadButtonState();
  } catch (error) {
    console.error("Error previewing attachments:", error);
    showResult("Failed to preview attachments and email. Please try again.", "error");
    updateUploadButtonState();
  } finally {
    hideProcessingMessage();
  }
};

/************************************************************************
 * 10. FAVORITES & METADATA
 ************************************************************************/

/**
 * Loads the user's saved favorite site/library configuration from RoamingSettings.
 * @returns A promise resolving to true if a favorite was loaded, false otherwise.
 */
const loadFavoriteConfiguration = async (): Promise<boolean> => {
  try {
    const accessToken = await getAccessToken();
    let userEmail = "unknown";
    try {
      const uRes = await fetchWithRetry("https://graph.microsoft.com/v1.0/me", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (uRes.ok) {
        const uData = await uRes.json();
        userEmail = uData.userPrincipalName || uData.mail || "unknown";
      }
    } catch (e) {
      console.warn("Error fetching user email from Graph API:", e);
    }

    if (userEmail === "unknown") return false;

    const targetSiteId = await resolveNativeSiteId(sharePointConfigSiteId, sharePointConfigListId);
    const url = `https://graph.microsoft.com/v1.0/sites/${targetSiteId}/lists/${sharePointConfigListId}/items?$filter=fields/Title eq '${userEmail}'&$expand=fields`;

    const response = await fetchWithRetry(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Prefer: "HonorNonIndexedQueriesWarningMayFailRandomly",
      },
    });
    if (response.ok) {
      const data = await response.json();
      if (data && data.value && data.value.length > 0) {
        const fields = data.value[0].fields;
        currentSiteId = fields.SiteId;
        currentSiteName = fields.SiteName;
        currentLibraryId = fields.LibraryId;
        currentLibraryName = fields.LibraryName;
        driveId = currentLibraryId;
        selectionMode = "folder";

        const h3 = getHTMLElement("selection-header");
        if (h3) h3.textContent = "Site and Library Selection";

        const saveFavCb = getHTMLElement("save-favorite-checkbox") as HTMLInputElement;
        if (saveFavCb) saveFavCb.checked = true;
        return true;
      }
    }
    return false;
  } catch (error) {
    console.error("Error loading favorite conf:", error);
    return false;
  }
};
// Delete the saved favorite for the current user from the SharePoint config list
/**
 * Deletes the user's saved favorite configuration from RoamingSettings.
 */
const deleteFavoriteConfiguration = async (): Promise<void> => {
  showProcessingMessage("Removing saved favorite...");
  try {
    const accessToken = await getAccessToken();
    let userEmail = "unknown";
    const uRes = await fetchWithRetry("https://graph.microsoft.com/v1.0/me", {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (uRes.ok) {
      const uData = await uRes.json();
      userEmail = uData.userPrincipalName || uData.mail;
    }
    const targetSiteId = await resolveNativeSiteId(sharePointConfigSiteId, sharePointConfigListId);
    const url = `https://graph.microsoft.com/v1.0/sites/${targetSiteId}/lists/${sharePointConfigListId}/items?$filter=fields/Title eq '${userEmail}'&$expand=fields`;
    const existingRes = await fetchWithRetry(url, {
      headers: { Authorization: `Bearer ${accessToken}`, Prefer: "HonorNonIndexedQueriesWarningMayFailRandomly" },
    });
    if (existingRes.ok) {
      const data = await existingRes.json();
      if (data.value && data.value.length > 0) {
        const itemId = data.value[0].id;
        const delRes = await fetchWithRetry(
          `https://graph.microsoft.com/v1.0/sites/${targetSiteId}/lists/${sharePointConfigListId}/items/${itemId}`,
          { method: "DELETE", headers: { Authorization: `Bearer ${accessToken}` } }
        );
        if (!delRes.ok && delRes.status !== 204) {
          throw new Error(`DELETE failed: ${delRes.statusText}`);
        }
      }
    }
  } catch (err: any) {
    console.error("Error deleting favorite:", err);
    showResult(`Failed to remove Favorite. Error: ${err.message}`, "error");
    // Re-check the box since deletion failed
    const cb = getHTMLElement("save-favorite-checkbox") as HTMLInputElement;
    if (cb) cb.checked = true;
  } finally {
    hideProcessingMessage();
  }
};
// Toggle handler: called whenever the Save Favorite checkbox changes
/**
 * Handles the toggle event for the 'Save Favorite' checkbox, saving or deleting the current configuration.
 */
const onSaveFavoriteToggle = async () => {
  const cb = getHTMLElement("save-favorite-checkbox") as HTMLInputElement;
  if (!cb) return;

  if (cb.checked) {
    // --- CHECKED: Save current site & library as favorite ---
    if (!currentSiteId || !currentLibraryId) {
      showResult("You must select a Site and a Library before saving as Favorite.", "warning");
      cb.checked = false;
      return;
    }
    showProcessingMessage("Saving favorite configuration...");
    try {
      const accessToken = await getAccessToken();
      let userEmail = "unknown";
      const uRes = await fetchWithRetry("https://graph.microsoft.com/v1.0/me", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (uRes.ok) {
        const uData = await uRes.json();
        userEmail = uData.userPrincipalName || uData.mail;
      }
      const targetSiteId = await resolveNativeSiteId(sharePointConfigSiteId, sharePointConfigListId);
      const queryUrl = `https://graph.microsoft.com/v1.0/sites/${targetSiteId}/lists/${sharePointConfigListId}/items?$filter=fields/Title eq '${userEmail}'`;
      const existingRes = await fetchWithRetry(queryUrl, {
        headers: { Authorization: `Bearer ${accessToken}`, Prefer: "HonorNonIndexedQueriesWarningMayFailRandomly" },
      });
      const payload = {
        fields: {
          Title: userEmail,
          SiteId: currentSiteId,
          SiteName: currentSiteName,
          LibraryId: currentLibraryId,
          LibraryName: currentLibraryName,
        },
      };
      if (existingRes.ok) {
        const data = await existingRes.json();
        if (data.value && data.value.length > 0) {
          const itemId = data.value[0].id;
          const patchRes = await fetchWithRetry(
            `https://graph.microsoft.com/v1.0/sites/${targetSiteId}/lists/${sharePointConfigListId}/items/${itemId}/fields`,
            {
              method: "PATCH",
              headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
              body: JSON.stringify(payload.fields),
            }
          );
          if (!patchRes.ok) {
            const e = await patchRes.json().catch(() => ({}));
            throw new Error(`PATCH Failed: ${e.error?.message || patchRes.statusText}`);
          }
        } else {
          const postRes = await fetchWithRetry(
            `https://graph.microsoft.com/v1.0/sites/${targetSiteId}/lists/${sharePointConfigListId}/items`,
            {
              method: "POST",
              headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
              body: JSON.stringify(payload),
            }
          );
          if (!postRes.ok) {
            const e = await postRes.json().catch(() => ({}));
            throw new Error(`POST Failed: ${e.error?.message || postRes.statusText}`);
          }
        }
        showTopBannerMessage(
          `<span style="color: #107c10;"><i class="fas fa-check-circle" style="color: #107c10;"></i> <b>Favorite Site &amp; Library saved successfully.</b></span>`
        );
      } else {
        const e = await existingRes.json().catch(() => ({}));
        throw new Error(`Query Failed: ${e.error?.message || existingRes.statusText}`);
      }
    } catch (err: any) {
      console.error("Error saving favorite:", err);
      showResult(`Failed to save Favorite. Error: ${err.message}`, "error");
      cb.checked = false;
    } finally {
      hideProcessingMessage();
    }
  } else {
    // --- UNCHECKED: Delete the saved favorite ---
    await deleteFavoriteConfiguration();
    await resetToSiteSelection();
  }
};
// Hides or shows ONLY the "Change Site / Library" link (so Save Favorite remains visible)
/**
 * Toggles the visibility of the 'Change Site' link in the UI.
 * @param visible Whether the link should be visible.
 */
const showChangeSiteLink = (visible: boolean) => {
  const link = getHTMLElement("change-site-link");
  if (link) {
    if (visible) {
      link.classList.remove("hidden");
    } else {
      link.classList.add("hidden");
    }
  }
};
/**
 * Toggles the visibility of the 'Save Favorite' checkbox container in the UI.
 * @param _visible Whether the container should be visible.
 */
const showSaveFavorite = (_visible: boolean) => {
  /* no-op */
};
/**
 * Resets the add-in state to the top-level site selection screen.
 */
const resetToSiteSelection = async () => {
  const selectionBody = getHTMLElement("folder-selection-body");
  if (selectionBody) selectionBody.classList.add("hidden");

  clearSearchInput();
  // Reset all state
  hasCustomColumnsFetched = false;
  currentMetadataValues = null;
  customColumnsConfig = [];
  selectionMode = "site";
  currentSiteId = "";
  currentSiteName = "";
  currentLibraryId = "";
  currentLibraryName = "";
  driveId = "";
  currentFolderId = "root";
  breadcrumbHistory = [];

  // Update UI
  showChangeSiteLink(false);
  showSaveFavorite(false); // this is a no-op, but retained just in case

  const saveFavCb = getHTMLElement("save-favorite-checkbox") as HTMLInputElement;
  if (saveFavCb) saveFavCb.checked = false; // visually uncheck when abandoning selection

  updateBreadcrumb();
  updateSelectionDisplay();

  // Load sites
  const sites = await fetchSites();
  updateFolderTable(sites, "root");
  updateUploadButtonState();
};
/**
 * Displays the metadata modal to collect custom column values before upload.
 */
const showMetadataModal = () => {
  const modal = getHTMLElement("metadata-modal");
  const container = getHTMLElement("metadata-form-container");
  if (!modal || !container) return;

  container.innerHTML = "";

  // Always clear any previous error message when opening the modal
  const errorMsgOnOpen = getHTMLElement("metadata-error-message");
  if (errorMsgOnOpen) {
    errorMsgOnOpen.textContent = "";
    errorMsgOnOpen.classList.add("hidden");
  }
  customColumnsConfig.forEach((col) => {
    const fieldDiv = document.createElement("div");
    fieldDiv.className = "metadata-field";

    const isRestricted = col.required || col.enforceUniqueValues;

    const label = document.createElement("label");
    label.textContent = col.displayName || col.name;
    if (isRestricted) {
      label.textContent += " *";
      label.style.fontWeight = "bold";
    }

    let input: any;

    if (col.choice) {
      if (col.choice.displayAs === "checkBoxes") {
        input = document.createElement("div");
        input.id = `meta-${col.name}`;
        input.className = "checkbox-group";
        input.style.maxHeight = "120px";
        input.style.overflowY = "auto";
        input.style.border = "1px solid #ccc";
        input.style.padding = "4px 8px";
        input.style.borderRadius = "2px";

        col.choice.choices.forEach((choice: string) => {
          const cbDiv = document.createElement("div");
          cbDiv.style.display = "flex";
          cbDiv.style.alignItems = "center";
          cbDiv.style.marginBottom = "4px";

          const cb = document.createElement("input");
          cb.type = "checkbox";
          cb.value = choice;
          cb.className = "meta-checkbox";
          cb.style.marginRight = "8px";
          cb.style.cursor = "pointer";
          cb.style.width = "auto";
          cb.style.padding = "0";
          cb.style.flexShrink = "0";

          const cbLabel = document.createElement("label");
          cbLabel.textContent = choice;
          cbLabel.style.fontWeight = "normal";
          cbLabel.style.margin = "0";
          cbLabel.style.cursor = "pointer";

          cbLabel.onclick = (e) => {
            e.preventDefault();
            cb.checked = !cb.checked;
          };

          cbDiv.appendChild(cb);
          cbDiv.appendChild(cbLabel);
          input.appendChild(cbDiv);
        });
      } else {
        input = document.createElement("select");
        input.id = `meta-${col.name}`;
        const emptyOption = document.createElement("option");
        emptyOption.value = "";
        emptyOption.text = "Select...";
        input.appendChild(emptyOption);

        col.choice.choices.forEach((choice: string) => {
          const option = document.createElement("option");
          option.value = choice;
          option.text = choice;
          input.appendChild(option);
        });
      }
    } else if (col.dateTime) {
      input = document.createElement("input");
      input.id = `meta-${col.name}`;
      input.type = "date";
      input.min = "1900-01-01";
      input.max = "9999-12-31";
    } else if (col.boolean) {
      input = document.createElement("select");
      input.id = `meta-${col.name}`;
      const emptyOption = document.createElement("option");
      emptyOption.value = "";
      emptyOption.text = "Select...";
      input.appendChild(emptyOption);
      const yesOption = document.createElement("option");
      yesOption.value = "true";
      yesOption.text = "Yes";
      input.appendChild(yesOption);
      const noOption = document.createElement("option");
      noOption.value = "false";
      noOption.text = "No";
      input.appendChild(noOption);
    } else {
      input = document.createElement("input");
      input.id = `meta-${col.name}`;
      input.type = "text";
    }

    if (isRestricted && (input instanceof HTMLInputElement || input instanceof HTMLSelectElement)) {
      input.required = true;
    }

    fieldDiv.appendChild(label);
    fieldDiv.appendChild(input);
    container.appendChild(fieldDiv);
  });

  modal.classList.remove("hidden");
};
/**
 * Closes the metadata modal.
 */
const closeMetadataModal = () => {
  const modal = getHTMLElement("metadata-modal");
  if (modal) modal.classList.add("hidden");

  // Clear error message when user cancels the modal
  const errorMsg = getHTMLElement("metadata-error-message");
  if (errorMsg) {
    errorMsg.textContent = "";
    errorMsg.classList.add("hidden");
  }
};
/**
 * Saves the data entered in the metadata modal and proceeds with the upload.
 */
const saveMetadata = async () => {
  const newValues: Record<string, any> = {};
  let isValid = true;
  const errorMsg = getHTMLElement("metadata-error-message");
  if (errorMsg) errorMsg.classList.add("hidden");

  customColumnsConfig.forEach((col) => {
    const input = getHTMLElement(`meta-${col.name}`);
    const isRestricted = col.required || col.enforceUniqueValues;

    if (input) {
      let val: any;
      if (col.choice && col.choice.displayAs === "checkBoxes") {
        const checkboxes = input.querySelectorAll('input[type="checkbox"]:checked');
        val = Array.from(checkboxes).map((cb: any) => cb.value);
      } else {
        val = (input as HTMLInputElement | HTMLSelectElement).value.trim();
      }

      const isEmpty = Array.isArray(val) ? val.length === 0 : val === "";

      if (isRestricted && isEmpty) {
        isValid = false;
        input.style.borderColor = "red";
      } else {
        input.style.borderColor = "#ccc";

        // Skip empty values so we don't send invalid empty strings to SharePoint (e.g., for Dates or Choices)
        if (!isEmpty) {
          // Convert to correct types for Graph API
          if (col.boolean) {
            val = val === "true";
          } else if (col.dateTime) {
            // "YYYY-MM-DD" needs to be converted to ISO string
            const dateObj = new Date(val);
            if (!isNaN(dateObj.getTime())) {
              val = dateObj.toISOString();
            }
          }

          if (Array.isArray(val)) {
            newValues[`${col.name}@odata.type`] = "Collection(Edm.String)";
            newValues[col.name] = val;
          } else {
            newValues[col.name] = val;
          }
        }
      }
    }
  });

  if (!isValid) {
    if (errorMsg) {
      errorMsg.textContent = "Please fill in all restricted (*) fields before uploading.";
      errorMsg.classList.remove("hidden");
    } else {
      showResult("Please fill in all restricted fields.", "warning");
    }
    return;
  }

  currentMetadataValues = newValues;
  closeMetadataModal();
  await proceedWithUpload();
};

/************************************************************************
 * UNCLASSIFIED / OTHERS
 ************************************************************************/

// Global APP_VERSION from webpack DefinePlugin
declare const APP_VERSION: string;
// Global error handlers (already excellent)
window.onerror = function (message, source, lineno, colno, error) {
  console.error("Global error:", { message, source, lineno, colno, error });
  showResult("An unexpected error occurred. Please try again.", "error");
  hideProcessingMessage(); // Ensure processing message is hidden on global error
  return true; // Prevent default error handling
};
window.addEventListener("unhandledrejection", (event) => {
  console.error("Unhandled rejection:", event.reason);
  showResult("An unexpected error occurred. Please try again.", "error");
  hideProcessingMessage(); // Ensure processing message is hidden on unhandled rejection
  event.preventDefault();
});